// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import { filter } from "../../../../io/hyperify/core/functions/filter";
import { find } from "../../../../io/hyperify/core/functions/find";
import { isEqual } from "../../../../io/hyperify/core/functions/isEqual";
import { keys } from "../../../../io/hyperify/core/functions/keys";
import { map } from "../../../../io/hyperify/core/functions/map";
import { reduce } from "../../../../io/hyperify/core/functions/reduce";
import { LogService } from "../../../../io/hyperify/core/LogService";
import { BackupType } from "../../../../io/hyperify/core/store/types/product/features/BackupType";
import { CpuShare } from "../../../../io/hyperify/core/store/types/product/features/CpuShare";
import { DiskType } from "../../../../io/hyperify/core/store/types/product/features/DiskType";
import { DiskUsageType } from "../../../../io/hyperify/core/store/types/product/features/DiskUsageType";
import { EmailOption } from "../../../../io/hyperify/core/store/types/product/features/EmailOption";
import { NetworkIpType } from "../../../../io/hyperify/core/store/types/product/features/NetworkIpType";
import { NetworkType } from "../../../../io/hyperify/core/store/types/product/features/NetworkType";
import { NetworkZone } from "../../../../io/hyperify/core/store/types/product/features/NetworkZone";
import { OperatingSystem } from "../../../../io/hyperify/core/store/types/product/features/OperatingSystem";
import { PHPVersion } from "../../../../io/hyperify/core/store/types/product/features/PHPVersion";
import { ProductFeature } from "../../../../io/hyperify/core/store/types/product/features/ProductFeature";
import { ProductFeatureCategory } from "../../../../io/hyperify/core/store/types/product/features/ProductFeatureCategory";
import { ProductFeatureCategoryMappingType } from "../../../../io/hyperify/core/store/types/product/features/ProductFeatureCategoryMappingType";
import { ProductFeatureId } from "../../../../io/hyperify/core/store/types/product/features/ProductFeatureId";
import { RaidType } from "../../../../io/hyperify/core/store/types/product/features/RaidType";
import { VirtualizationType } from "../../../../io/hyperify/core/store/types/product/features/VirtualizationType";
import { WebServer } from "../../../../io/hyperify/core/store/types/product/features/WebServer";
import { Product } from "../../../../io/hyperify/core/store/types/product/Product";
import { ProductPriceType } from "../../../../io/hyperify/core/store/types/product/ProductPriceType";
import { ProductType } from "../../../../io/hyperify/core/store/types/product/ProductType";
import { SendanorProductId } from "../types/SendanorProductId";
import { SendanorProductUtils } from "../utils/SendanorProductUtils";
import {
    T_ATLAS_EXTRA_SUMMARY,
    T_ATLAS_EXTRA_TITLE,
    T_ATLAS_PRODUCT_ATLAS_18G_DESCRIPTION,
    T_ATLAS_PRODUCT_ATLAS_18G_TITLE,
    T_EMAIL_EXTRA_SUMMARY,
    T_EMAIL_EXTRA_TITLE,
    T_EMAIL_PRODUCT_1_DESCRIPTION,
    T_EMAIL_PRODUCT_1_TITLE,
    T_EMAIL_PRODUCT_2_DESCRIPTION,
    T_EMAIL_PRODUCT_2_TITLE,
    T_EMAIL_PRODUCT_3_DESCRIPTION,
    T_EMAIL_PRODUCT_3_TITLE,
    T_EMAIL_PRODUCT_MINI_DESCRIPTION,
    T_EMAIL_PRODUCT_MINI_TITLE,
    T_NET_EXTRA_DESCRIPTION,
    T_NET_EXTRA_TITLE,
    T_SHELL_PRODUCT_1_SUMMARY,
    T_SHELL_PRODUCT_1_TITLE,
    T_SHELL_PRODUCT_2_SUMMARY,
    T_SHELL_PRODUCT_2_TITLE,
    T_SOFTWARE_GOVM_PRODUCT_1_SUMMARY,
    T_SOFTWARE_GOVM_PRODUCT_1_TITLE,
    T_SOFTWARE_GOVM_PRODUCT_2_SUMMARY,
    T_SOFTWARE_GOVM_PRODUCT_2_TITLE,
    T_VIRTUAL_SERVER_EXTRA_SUMMARY,
    T_VIRTUAL_SERVER_EXTRA_TITLE,
    T_VIRTUALSERVERS_PRODUCT_10_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_10_TITLE,
    T_VIRTUALSERVERS_PRODUCT_11_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_11_TITLE,
    T_VIRTUALSERVERS_PRODUCT_12_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_12_TITLE,
    T_VIRTUALSERVERS_PRODUCT_13_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_13_TITLE,
    T_VIRTUALSERVERS_PRODUCT_14_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_14_TITLE,
    T_VIRTUALSERVERS_PRODUCT_1_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_1_TITLE,
    T_VIRTUALSERVERS_PRODUCT_2_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_2_TITLE,
    T_VIRTUALSERVERS_PRODUCT_3_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_3_TITLE,
    T_VIRTUALSERVERS_PRODUCT_4_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_4_TITLE,
    T_VIRTUALSERVERS_PRODUCT_6_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_6_TITLE,
    T_VIRTUALSERVERS_PRODUCT_7_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_7_TITLE,
    T_VIRTUALSERVERS_PRODUCT_8_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_8_TITLE,
    T_VIRTUALSERVERS_PRODUCT_MINI_1_DESCRIPTION,
    T_VIRTUALSERVERS_PRODUCT_MINI_1_TITLE,
    T_WEB_EXTRA_SUMMARY,
    T_WEB_EXTRA_TITLE,
    T_WEBHOTELS_PRODUCT_ATLAS_10G_DESCRIPTION,
    T_WEBHOTELS_PRODUCT_ATLAS_10G_TITLE,
    T_WEBHOTELS_PRODUCT_ATLAS_20G_DESCRIPTION,
    T_WEBHOTELS_PRODUCT_ATLAS_20G_TITLE,
    T_WEBHOTELS_PRODUCT_ATLAS_2G_DESCRIPTION,
    T_WEBHOTELS_PRODUCT_ATLAS_2G_TITLE,
    T_WEBHOTELS_PRODUCT_ATLAS_5G_DESCRIPTION,
    T_WEBHOTELS_PRODUCT_ATLAS_5G_TITLE,
    T_WEBHOTELS_PRODUCT_TITAN_10G_DESCRIPTION,
    T_WEBHOTELS_PRODUCT_TITAN_10G_TITLE,
    T_WEBHOTELS_PRODUCT_TITAN_1G_DESCRIPTION,
    T_WEBHOTELS_PRODUCT_TITAN_1G_TITLE,
    T_WEBHOTELS_PRODUCT_TITAN_20G_DESCRIPTION,
    T_WEBHOTELS_PRODUCT_TITAN_20G_TITLE,
    T_WEBHOTELS_PRODUCT_TITAN_2G_DESCRIPTION,
    T_WEBHOTELS_PRODUCT_TITAN_2G_TITLE,
    T_WEBHOTELS_PRODUCT_TITAN_5G_DESCRIPTION,
    T_WEBHOTELS_PRODUCT_TITAN_5G_TITLE,
} from "./translation";

const LOG = LogService.createLogger( 'products' );

export const DEFAULT_VAT_PERCENT = 0.255;

export const FI_DOMAIN_RETAIL_PRICE = 12;

const SHORT_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS = 250;
const SHORT_DOMAIN_PRICE_INCLUDING_VAT_1_YEAR = Math.ceil(SHORT_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS - (10-1)*FI_DOMAIN_RETAIL_PRICE*(1+DEFAULT_VAT_PERCENT));
const SHORT_DOMAIN_PRICE_INCLUDING_VAT_3_YEARS = Math.ceil(SHORT_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS - (10-3)*FI_DOMAIN_RETAIL_PRICE*(1+DEFAULT_VAT_PERCENT));
const SHORT_DOMAIN_PRICE_INCLUDING_VAT_5_YEARS = Math.ceil(SHORT_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS - (10-5)*FI_DOMAIN_RETAIL_PRICE*(1+DEFAULT_VAT_PERCENT));
const SHORT_DOMAIN_VAT_PERCENT = DEFAULT_VAT_PERCENT;

const SHORT_DOMAINS_ON_SALE = [
    "4o.fi",
    "5j.fi",
    "5l.fi",
    "5n.fi",
    "5u.fi",
    "5z.fi",
    "6i.fi",
    "6l.fi",
    "6o.fi",
    "6u.fi",
    "6w.fi",
    "6z.fi",
    "7j.fi",
    "7l.fi",
    "7o.fi",
    "7r.fi",
    "8e.fi",
    "8l.fi",
    "8m.fi",
    "8n.fi",
    "8t.fi",
    "8u.fi",
    "8y.fi",
    "9f.fi",
    "9i.fi",
    "9l.fi",
    "9m.fi",
    "9y.fi",
    "8z.fi",
    "9o.fi",
    "6j.fi",
];

export const COM_DOMAIN_RETAIL_PRICE = 12;

const STANDARD_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS = 150 + 25*10;
const STANDARD_DOMAIN_PRICE_INCLUDING_VAT_1_YEAR = Math.ceil(STANDARD_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS - (10-1)*COM_DOMAIN_RETAIL_PRICE*(1+DEFAULT_VAT_PERCENT));
const STANDARD_DOMAIN_PRICE_INCLUDING_VAT_3_YEARS = Math.ceil(STANDARD_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS - (10-3)*COM_DOMAIN_RETAIL_PRICE*(1+DEFAULT_VAT_PERCENT));
const STANDARD_DOMAIN_PRICE_INCLUDING_VAT_5_YEARS = Math.ceil(STANDARD_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS - (10-5)*COM_DOMAIN_RETAIL_PRICE*(1+DEFAULT_VAT_PERCENT));
const STANDARD_DOMAIN_VAT_PERCENT = DEFAULT_VAT_PERCENT;

const STANDARD_DOMAINS_ON_SALE = [
    "ohjelmistoarkkitehti.com",
    "ohjelmistoarkkitehti.fi",
    "ohjelmistoarkkitehti.net",
    "ohjelmoijat.net",
    "ohjelmoijat.com",
    "gamechix.com",
    "jakeapp.com",
    "koodaajat.com",
    "webkehittaja.com",
    "webkonsultti.com",
    "webkehittaja.fi",
    "webkonsultti.fi",
    "sysadm.fi",
    "webkonsultti.net",
    "yrityksellekotisivu.com",
    "yritykselleverkkokauppa.com",
    "webkehittäjä.com",
    "webkehittäjä.fi",
    "webkehittäjä.net",
    "sää.info",
];

export const PREMIUM_DOMAIN_RETAIL_PRICE = 30;

const PREMIUM_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS = 5000 + 30*10;
const PREMIUM_DOMAIN_PRICE_INCLUDING_VAT_1_YEAR = Math.ceil(PREMIUM_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS - (10-1)*PREMIUM_DOMAIN_RETAIL_PRICE*(1+DEFAULT_VAT_PERCENT));
const PREMIUM_DOMAIN_PRICE_INCLUDING_VAT_3_YEARS = Math.ceil(PREMIUM_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS - (10-3)*PREMIUM_DOMAIN_RETAIL_PRICE*(1+DEFAULT_VAT_PERCENT));
const PREMIUM_DOMAIN_PRICE_INCLUDING_VAT_5_YEARS = Math.ceil(PREMIUM_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS - (10-5)*PREMIUM_DOMAIN_RETAIL_PRICE*(1+DEFAULT_VAT_PERCENT));
const PREMIUM_DOMAIN_VAT_PERCENT = DEFAULT_VAT_PERCENT;

const PREMIUM_DOMAINS_ON_SALE = [
    "m2m.center",
    "module.center",
    "node.center",
    "startup.center",
    "k18.io",
    "huuto.app",
    "huutokauppa.app",
    "deitti.app",
    "viesti.app",
    "kirpputori.app",
    "deittailu.net",
    "battle.center",
    "disk.center",
    "git.center",
    "lan.center",
];

export const MINIMUM_DOMAIN_FEE_EUR : number = 2;
export const DOMAIN_PRICE_FACTOR : number = 0.1;

// VPS
export const PRODUCT_VPS_FEATURE_IDS_BY_CATEGORY : ProductFeatureCategoryMappingType = {
    [ProductFeatureCategory.CPU] : [
        ProductFeatureId.CPU_SHARE,
        ProductFeatureId.CPU_AMOUNT,
    ],
    [ProductFeatureCategory.MEMORY] : [
        ProductFeatureId.MEMORY_SIZE,
    ],
    [ProductFeatureCategory.DISK] : [
        ProductFeatureId.DISK_TYPE,
        ProductFeatureId.DISK_SIZE,
        ProductFeatureId.DISK_RAID,
        ProductFeatureId.DISK_BACKUP,
        ProductFeatureId.DISK_USAGE,
    ],
    [ProductFeatureCategory.DISK_2] : [
        ProductFeatureId.DISK_2_TYPE,
        ProductFeatureId.DISK_2_SIZE,
        ProductFeatureId.DISK_2_RAID,
        ProductFeatureId.DISK_2_BACKUP,
        ProductFeatureId.DISK_2_USAGE,
    ],
    [ProductFeatureCategory.DISK_3] : [
        ProductFeatureId.DISK_3_TYPE,
        ProductFeatureId.DISK_3_SIZE,
        ProductFeatureId.DISK_3_RAID,
        ProductFeatureId.DISK_3_BACKUP,
        ProductFeatureId.DISK_3_USAGE,
    ],
    [ProductFeatureCategory.NETWORK] : [
        ProductFeatureId.NETWORK_TYPE,
        ProductFeatureId.NETWORK_TRAFFIC,
        ProductFeatureId.NETWORK_ZONE,
        ProductFeatureId.NETWORK_IP4,
        ProductFeatureId.NETWORK_IP6,
        ProductFeatureId.NETWORK_NET6,
    ],
    [ProductFeatureCategory.VPS] : [
        ProductFeatureId.VPS_TYPE,
        ProductFeatureId.VPS_OS,
    ],
};
export const PRODUCT_VPS_FEATURE_CATEGORIES : ProductFeatureCategory[] = keys(
    PRODUCT_VPS_FEATURE_IDS_BY_CATEGORY,
);

// VPS
export const PRODUCT_NET_FEATURE_IDS_BY_CATEGORY : ProductFeatureCategoryMappingType = {
    [ProductFeatureCategory.NETWORK] : [
        ProductFeatureId.NETWORK_TYPE,
        ProductFeatureId.NETWORK_ZONE,
        ProductFeatureId.NETWORK_IP4,
        ProductFeatureId.NETWORK_IP6,
        ProductFeatureId.NETWORK_NET6,
    ],
};
export const PRODUCT_NET_FEATURE_CATEGORIES : ProductFeatureCategory[] = keys(
    PRODUCT_NET_FEATURE_IDS_BY_CATEGORY,
);

// Webhotels
export const PRODUCT_WEBHOTEL_FEATURE_IDS_BY_CATEGORY : ProductFeatureCategoryMappingType = {
    [ProductFeatureCategory.DISK] : [
        ProductFeatureId.DISK_TYPE,
        ProductFeatureId.DISK_SIZE,
        ProductFeatureId.DISK_RAID,
        ProductFeatureId.DISK_BACKUP,
    ],
    [ProductFeatureCategory.DOMAINS] : [
        ProductFeatureId.DOMAIN_COUNT,
        ProductFeatureId.DOMAIN_ALIAS_COUNT,
        ProductFeatureId.SUB_DOMAIN_COUNT,
    ],
    [ProductFeatureCategory.NETWORK] : [
        ProductFeatureId.NETWORK_TYPE,
        ProductFeatureId.NETWORK_TRAFFIC,
        ProductFeatureId.NETWORK_ZONE,
        ProductFeatureId.NETWORK_IP4,
        ProductFeatureId.NETWORK_IP6,
    ],
    [ProductFeatureCategory.VPS] : [
        ProductFeatureId.VPS_TYPE,
        ProductFeatureId.VPS_OS,
    ],
    [ProductFeatureCategory.WEBSERVER] : [
        ProductFeatureId.WEBSERVER_SOFTWARE,
        ProductFeatureId.PHP_SOFTWARE,
        ProductFeatureId.PHP_MEMORY,
        ProductFeatureId.SSL_CERTIFICATES,
        ProductFeatureId.SFTP_ACCOUNT_COUNT,
        ProductFeatureId.SSH_ACCOUNT_COUNT,
        ProductFeatureId.HTTP_2_SUPPORT,
        ProductFeatureId.HTACCESS_SUPPORT,
        ProductFeatureId.PASSWORD_PROTECTED_WEB_FOLDER_SUPPORT,
        ProductFeatureId.CUSTOM_ERROR_PAGE_SUPPORT,
        ProductFeatureId.NODE_JS_SOFTWARE,
        ProductFeatureId.CGI,
        ProductFeatureId.PERL_SOFTWARE,
        ProductFeatureId.PYTHON_SOFTWARE,
        ProductFeatureId.CRON,
    ],

    [ProductFeatureCategory.DATABASES] : [
        ProductFeatureId.DATABASE_COUNT,
        ProductFeatureId.MYSQL_SUPPORT,
        ProductFeatureId.MARIADB_SUPPORT,
        ProductFeatureId.POSTGRESQL_SUPPORT,
        // ProductFeatureId.REDIS_SUPPORT,
        // ProductFeatureId.MONGODB_SUPPORT,
        // ProductFeatureId.ELASTICSEARCH_SUPPORT,
        ProductFeatureId.PHPMYADMIN_SUPPORT,
    ],

    [ProductFeatureCategory.EMAILS] : [
        ProductFeatureId.MAILBOX_COUNT,
        ProductFeatureId.MAILBOX_ALIAS_COUNT,
        ProductFeatureId.DOMAIN_QUOTA,
        ProductFeatureId.ADMIN_USER_COUNT,
        ProductFeatureId.DOMAIN_MESSAGE_SENDING_LIMIT,
        ProductFeatureId.MAILBOX_QUOTA,
        ProductFeatureId.MAILBOX_MESSAGE_SENDING_LIMIT,
        ProductFeatureId.AUTOREPLY_SUPPORT,
        ProductFeatureId.ROUNDCUBE_SOFTWARE,
        ProductFeatureId.IMAP_SUPPORT,
        ProductFeatureId.POP_SUPPORT,
        ProductFeatureId.SMTP_AUTH_SUPPORT,
        ProductFeatureId.SPAM_FILTERING,
        ProductFeatureId.CLAMAV_SUPPORT,
        ProductFeatureId.DKIM_SUPPORT,
        ProductFeatureId.SPF_SUPPORT,
        ProductFeatureId.SIEVE_FILTERING_SUPPORT,
    ],
    [ProductFeatureCategory.EMAIL_ADMIN] : [
        ProductFeatureId.API_SUPPORT,
        ProductFeatureId.TOTP_2FA_SUPPORT,
        ProductFeatureId.OAUTH2_SUPPORT,
        ProductFeatureId.QUOTA_SUPPORT,
        ProductFeatureId.STATISTICS_SUPPORT,
        ProductFeatureId.MONITORING_SUPPORT,
    ],
    [ProductFeatureCategory.EMAIL_SERVER] : [
        ProductFeatureId.USER_THROTTLE_RATE,
        ProductFeatureId.DDOS_THROTTLE_RATE,
        ProductFeatureId.DDOS_LESSER_THROTTLE_RATE,
        ProductFeatureId.LOGIN_THROTTLE_RATE,
        ProductFeatureId.PASSWORD_RECOVERY_THROTTLE_RATE,
        ProductFeatureId.PASSWORD_RECOVERY_TOTP_THROTTLE_RATE,
        ProductFeatureId.PASSWORD_RECOVERY_APPLY_THROTTLE_RATE,
    ],

};
export const PRODUCT_WEBHOTEL_FEATURE_CATEGORIES : ProductFeatureCategory[] = keys(
    PRODUCT_WEBHOTEL_FEATURE_IDS_BY_CATEGORY,
);

// Email
export const PRODUCT_EMAIL_FEATURE_IDS_BY_CATEGORY : ProductFeatureCategoryMappingType = {
    [ProductFeatureCategory.EMAILS] : [
        ProductFeatureId.MAILBOX_COUNT,
        ProductFeatureId.MAILBOX_ALIAS_COUNT,
        ProductFeatureId.DOMAIN_QUOTA,
        ProductFeatureId.EMAIL_DOMAIN_COUNT,
        ProductFeatureId.EMAIL_DOMAIN_ALIAS_COUNT,
        ProductFeatureId.ADMIN_USER_COUNT,
        ProductFeatureId.DOMAIN_MESSAGE_SENDING_LIMIT,
        ProductFeatureId.MAILBOX_QUOTA,
        ProductFeatureId.MAILBOX_MESSAGE_SENDING_LIMIT,
        ProductFeatureId.AUTOREPLY_SUPPORT,
        ProductFeatureId.ROUNDCUBE_SOFTWARE,
        ProductFeatureId.IMAP_SUPPORT,
        ProductFeatureId.POP_SUPPORT,
        ProductFeatureId.SMTP_AUTH_SUPPORT,
        ProductFeatureId.SPAM_FILTERING,
        ProductFeatureId.CLAMAV_SUPPORT,
        ProductFeatureId.DKIM_SUPPORT,
        ProductFeatureId.SPF_SUPPORT,
        ProductFeatureId.SIEVE_FILTERING_SUPPORT,
    ],
    [ProductFeatureCategory.EMAIL_ADMIN] : [
        ProductFeatureId.API_SUPPORT,
        ProductFeatureId.TOTP_2FA_SUPPORT,
        ProductFeatureId.OAUTH2_SUPPORT,
        ProductFeatureId.QUOTA_SUPPORT,
        ProductFeatureId.STATISTICS_SUPPORT,
        ProductFeatureId.MONITORING_SUPPORT,
    ],
    [ProductFeatureCategory.EMAIL_SERVER] : [
        ProductFeatureId.USER_THROTTLE_RATE,
        ProductFeatureId.DDOS_THROTTLE_RATE,
        ProductFeatureId.DDOS_LESSER_THROTTLE_RATE,
        ProductFeatureId.LOGIN_THROTTLE_RATE,
        ProductFeatureId.PASSWORD_RECOVERY_THROTTLE_RATE,
        ProductFeatureId.PASSWORD_RECOVERY_TOTP_THROTTLE_RATE,
        ProductFeatureId.PASSWORD_RECOVERY_APPLY_THROTTLE_RATE,
    ],
    [ProductFeatureCategory.NETWORK] : [
        ProductFeatureId.NETWORK_ZONE,
    ],
    [ProductFeatureCategory.DISK] : [
        ProductFeatureId.DISK_SIZE,
        ProductFeatureId.DISK_TYPE,
        ProductFeatureId.DISK_RAID,
        ProductFeatureId.DISK_BACKUP,
    ],
};
export const PRODUCT_EMAIL_FEATURE_CATEGORIES : ProductFeatureCategory[] = keys(
    PRODUCT_EMAIL_FEATURE_IDS_BY_CATEGORY,
);

// Shell
export const PRODUCT_SHELL_FEATURE_IDS_BY_CATEGORY : ProductFeatureCategoryMappingType = {
    [ProductFeatureCategory.DISK] : [
        ProductFeatureId.DISK_TYPE,
        ProductFeatureId.DISK_SIZE,
        ProductFeatureId.DISK_RAID,
        ProductFeatureId.DISK_BACKUP,
    ],
    [ProductFeatureCategory.NETWORK] : [
        ProductFeatureId.NETWORK_ZONE,
    ],
};
export const PRODUCT_SHELL_FEATURE_CATEGORIES : ProductFeatureCategory[] = keys(
    PRODUCT_SHELL_FEATURE_IDS_BY_CATEGORY,
);

// Software
export const PRODUCT_SOFTWARE_FEATURE_IDS_BY_CATEGORY : ProductFeatureCategoryMappingType = {
};
export const PRODUCT_SOFTWARE_FEATURE_CATEGORIES : ProductFeatureCategory[] = keys(
    PRODUCT_SOFTWARE_FEATURE_IDS_BY_CATEGORY,
);

export const DEFAULT_DISCOUNT_PERCENT : number | undefined = 0.25;
export const DEFAULT_DISCOUNT_FROM : string | undefined = '2023-11-25T00:00:00Z';
export const DEFAULT_DISCOUNT_TO : string | undefined = '2023-11-30T21:59:59Z';

export const VPS_DISCOUNT_PERCENT : number | undefined = DEFAULT_DISCOUNT_PERCENT;
export const VPS_DISCOUNT_FROM : string | undefined = DEFAULT_DISCOUNT_FROM;
export const VPS_DISCOUNT_TO : string | undefined = DEFAULT_DISCOUNT_TO;

export const ATLAS_DISCOUNT_PERCENT : number | undefined = 0.50;
export const ATLAS_DISCOUNT_FROM : string | undefined = '2024-03-27T00:00:00Z';
export const ATLAS_DISCOUNT_TO : string | undefined = '2024-04-30T20:59:59Z';

export const ATLAS_EXTRA_DISCOUNT_PERCENT : number | undefined = DEFAULT_DISCOUNT_PERCENT;
export const ATLAS_EXTRA_DISCOUNT_FROM : string | undefined = DEFAULT_DISCOUNT_FROM;
export const ATLAS_EXTRA_DISCOUNT_TO : string | undefined = DEFAULT_DISCOUNT_TO;

export const WEB_DISCOUNT_PERCENT : number | undefined = DEFAULT_DISCOUNT_PERCENT;
export const WEB_DISCOUNT_FROM : string | undefined = DEFAULT_DISCOUNT_FROM;
export const WEB_DISCOUNT_TO : string | undefined = DEFAULT_DISCOUNT_TO;

export const EMAIL_DISCOUNT_PERCENT : number | undefined = DEFAULT_DISCOUNT_PERCENT;
export const EMAIL_DISCOUNT_FROM : string | undefined = DEFAULT_DISCOUNT_FROM;
export const EMAIL_DISCOUNT_TO : string | undefined = DEFAULT_DISCOUNT_TO;

export const SHELL_DISCOUNT_PERCENT : number | undefined = DEFAULT_DISCOUNT_PERCENT;
export const SHELL_DISCOUNT_FROM : string | undefined = DEFAULT_DISCOUNT_FROM;
export const SHELL_DISCOUNT_TO : string | undefined = DEFAULT_DISCOUNT_TO;

export const SOFTWARE_DISCOUNT_PERCENT : number | undefined = DEFAULT_DISCOUNT_PERCENT;
export const SOFTWARE_DISCOUNT_FROM : string | undefined = DEFAULT_DISCOUNT_FROM;
export const SOFTWARE_DISCOUNT_TO : string | undefined = DEFAULT_DISCOUNT_TO;

export const WP_DISCOUNT_PERCENT : number | undefined = DEFAULT_DISCOUNT_PERCENT;
export const WP_DISCOUNT_FROM : string | undefined = DEFAULT_DISCOUNT_FROM;
export const WP_DISCOUNT_TO : string | undefined = DEFAULT_DISCOUNT_TO;

export const NET_DISCOUNT_PERCENT_MONTHLY : number | undefined = 0.10;
export const NET_DISCOUNT_PERCENT_YEARLY : number | undefined = 0.05;
export const NET_DISCOUNT_FROM : string | undefined = DEFAULT_DISCOUNT_FROM;
export const NET_DISCOUNT_TO : string | undefined = DEFAULT_DISCOUNT_TO;

export const SUPPORT_HOURLY_FEE = Math.ceil( 40 * (1+DEFAULT_VAT_PERCENT) );


/**
 * Mini 512 MB, 1/10 CPU, 1 Gbps, 8 GB SSD (7+1 GB), 0 TB HDD
 */
export const VPS_MINI_1_MONHTLY_PRICE = 5;

/**
 * Tavallinen 1 GB, 2x 1/10 CPU, 1 Gbps, 14 GB SSD (12+2 GB), 0 TB HDD
 */
export const VPS_1_MONHTLY_PRICE = 9;

/**
 * Tavaton 2 GB, 3x 1/10 CPU, 1 Gbps, 18 GB SSD (16+2 GB), 1 TB HDD
 */
export const VPS_6_MONHTLY_PRICE = 16;

/**
 * Mahtava 4 GB, 4x 1/10 CPU, 1 Gbps, 28 GB SSD (24+4 GB), 2 TB HDD
 */
export const VPS_7_MONHTLY_PRICE = 32;

/**
 * Valtava 8 GB, 6x 1/10 CPU, 1 Gbps, 40 GB SSD (32+8 GB), 4 TB HDD
 */
export const VPS_8_MONHTLY_PRICE = 65;


/**
 * Mini 512 MB, 1/10 CPU, 1 Gbps, 8 GB SSD (7+1 GB), 1 TB HDD
 */
export const VPS_10_MONHTLY_PRICE = 4.82;

/**
 * Tavallinen 1 GB, 2x 1/10 CPU, 1 Gbps, 8 GB SSD (7+1 GB), 1 TB HDD
 */
export const VPS_11_MONHTLY_PRICE = VPS_10_MONHTLY_PRICE * 2;

/**
 * Tavaton 2 GB, 4x 1/10 CPU, 1 Gbps, 8 GB SSD (7+1 GB), 1 TB HDD
 */
export const VPS_12_MONHTLY_PRICE = VPS_11_MONHTLY_PRICE * 2;

/**
 * Mahtava 4 GB, 8x 1/10 CPU, 1 Gbps, 8 GB SSD (7+1 GB), 1 TB HDD
 */
export const VPS_13_MONHTLY_PRICE = VPS_12_MONHTLY_PRICE * 2;

/**
 * Valtava 8 GB, 16x 1/10 CPU, 1 Gbps, 8 GB SSD (7+1 GB), 1 TB HDD
 */
export const VPS_14_MONHTLY_PRICE = VPS_13_MONHTLY_PRICE * 2;


/**
 * + 1 x 1/10 CPU, min 2 GHz
 */
export const VPS_EXTRA_CPU_1_10_MONHTLY_PRICE = 2;

/**
 * + 512 MB ram
 */
export const VPS_EXTRA_VPS_MEMORY_MINI_1_MONHTLY_PRICE = 2;

/**
 * + 1 GB ram
 */
export const VPS_EXTRA_VPS_MEMORY_1_MONHTLY_PRICE = 4;

/**
 * + IPv6-network /56
 */
export const VPS_EXTRA_VPS_NET6_1_MONHTLY_PRICE = 3;

/**
 * + IPv4-address
 */
export const VPS_EXTRA_VPS_IP4_1_MONHTLY_PRICE = 6;

/**
 * + 2 Gbps
 */
export const VPS_EXTRA_VPS_ETH2G_1_MONHTLY_PRICE = 5;

/**
 * + 5 Gbps
 */
export const VPS_EXTRA_VPS_ETH5G_1_MONHTLY_PRICE = 10;

/**
 * + 10 Gbps
 */
export const VPS_EXTRA_VPS_ETH10G_1_MONHTLY_PRICE = 20;

/**
 * + 250 GB, 7K SAS 12 Gbps
 */
export const VPS_EXTRA_VPS_SAS_250_MONHTLY_PRICE = 1.5; // Upcloud 0.056 €/GB/kk = 15 €/kk

/**
 * + 500 GB, 7K SAS 12 Gbps
 */
export const VPS_EXTRA_VPS_SAS_500_MONHTLY_PRICE = 2.5; // Upcloud 0.056 €/GB/kk = 28 €/kk

/**
 * + 1 TB, 7K SAS 12 Gbps
 */
export const VPS_EXTRA_VPS_SAS_1000_MONHTLY_PRICE = 4.8; // Upcloud 0.056 €/GB/kk = 56 €/kk; Hetzner storage box 4.02 €/kk/1 TB sis. ALV 25.5%

/**
 * + Levytila 10 GB, NVMe M2 SSD
 */
export const VPS_EXTRA_VPS_NVME_10_MONHTLY_PRICE = Math.ceil( 0.22 * 10 * (1+DEFAULT_VAT_PERCENT) *10 ) / 10;

/**
 * + Ylläpito- ja tuki 1 tunti/kk
 */
export const VPS_EXTRA_VPS_SUPPORT_1_MONHTLY_PRICE = SUPPORT_HOURLY_FEE / 8;

/**
 * + Tietoturvapäivitykset 1 vps/kk
 */
export const VPS_EXTRA_VPS_UPGRADE_1_MONHTLY_PRICE = 25;

/**
 * Varmuuskopiopalautus 1 tunti/kk
 */
export const VPS_EXTRA_VPS_BACKUP_RESTORE_1_MONHTLY_PRICE = SUPPORT_HOURLY_FEE / 10;

/**
 * Verkkosivujen asennus ja ylläpito
 *
 * Palvelu sisältää WordPressin asennuksen ja tietoturvapäivitykset yhdelle
 * verkkosivustolle. Jos valitset tuntihinnan, laskutetaan työ toteutuneen
 * työajan mukaan.
 *
 * Hintaan sisältyy päivitykset Wordpressin mukana tuleville osille tuoreimpaan
 * saatavilla olevaan versioon. Kausipalvelut eivät sisällä työtä asiakkaan
 * omille tai muista lähteistä ostetuille kaupallisille lisäosille, jotka
 * voidaan maksaa toteutuneiden kulujen mukaisesti.
 *
 * Tarvitset tämän tuotteen lisäksi myös webhotellipalvelun tai
 * virtuaalipalvelimen. Webhotellin ei tarvitse olla Sendanorilta, mutta
 * SSH-yhteys ja WordPressin vaatimukset täyttävä web-palvelin tarvitaan.
 */
export const WP_1_MONTHLY_PRICE = Math.round( SUPPORT_HOURLY_FEE / 5.5 );

/**
 *
 */
export const WP_1_HOURLY_PRICE = SUPPORT_HOURLY_FEE;

/**
 * Nopea ja viiveetön 10 Gbit/s verkkoyhteys
 *
 * Myymme nopeita valokuituyhteyksiä 10 Gbps nopeudella (best effort) Tampereen
 * ja Turun alueelle. Toimitusaika noin neljä viikkoa. Kysy tarjous muualle
 * Suomeen.
 *
 * Palvelumme on saatavilla yrityksille ja talonyhtiöille. Yhteyden
 * toteuttaminen edellyttää kiinteistöltä valokuitu- ja sisäverkkokaapelointia
 * sekä soveltuvia verkkolaitteita. Lisämaksusta voimme tarjota myös laitteet
 * ja niiden ylläpidon.
 *
 * Verkkoyhteyksiemme avulla useampi työntekijänne pääsee verkkoon
 * ruuhkattomasti ja voitte hyödyntää Sendanorin laitesalipalveluita täydellä
 * vauhdilla.
 */
export const NET_1_MONTHLY_PRICE = Math.ceil( 900 * (1+DEFAULT_VAT_PERCENT) );

/**
 * Palvelua hyödynnetään moderneissa sovelluksissa, kuten videopuheluissa ja
 * pikaviestimissä, joissa ohjelmistojen on tarkoitus keskustella suoraan
 * keskenään.
 *
 * Palvelumme tarjoaa kuusi ympäri maailmaa hajautettua TURN/STUN-palvelinta
 * WebRTC-yhteyksille.
 *
 * Sendanorin palvelimet sijaitsevat Suomessa, Saksassa, USA:ssa ja Singaporessa.
 * Jos tämä ei riitä, ota yhteyttä asiakaspalveluumme.
 */
export const WEBRTC_1_MONTHLY_PRICE = Math.ceil( 10 * (1+DEFAULT_VAT_PERCENT) );


/**
 * + 5 GB
 */
export const EXTRA_WEB_HOTEL_DISK_5_MONTHLY_PRICE = 5;

/**
 * + 25 GB
 */
export const EXTRA_WEB_HOTEL_DISK_25_MONTHLY_PRICE = 5 * 4;

/**
 * + 50 GB
 */
export const EXTRA_WEB_HOTEL_DISK_50_MONTHLY_PRICE = 5 * 7;

/**
 * + Ylläpito ja tuki, 1 tunti/kk
 */
export const EXTRA_WEB_SUPPORT_1_MONTHLY_PRICE = SUPPORT_HOURLY_FEE / 8;

/**
 * + Ylläpito ja tuki, 1 tunti/kk
 * @see https://github.com/sendanor/project-sendanor.fi/issues/129
 */
export const EXTRA_ATLAS_SUPPORT_1_MONTHLY_PRICE = 65;

/**
 * + Käyttäjätunnus (1 kpl/kk)
 * @see https://github.com/sendanor/project-sendanor.fi/issues/129
 */
export const EXTRA_ATLAS_USER_1_MONTHLY_PRICE = 10;

/**
 * + Varmuuskopioiden palautus (1 tunti/kk)
 */
export const EXTRA_WEB_HOTEL_BACKUP_RESTORE_1_MONTHLY_PRICE = SUPPORT_HOURLY_FEE / 10;

/**
 * + Käyttäjätunnus (1 kpl/kk)
 */
export const EXTRA_WEB_HOTEL_USER_1_MONTHLY_PRICE = 0.5;

/**
 * + MySQL-tietokanta (1 kpl/kk)
 */
export const EXTRA_WEB_HOTEL_MYSQL_1_MONTHLY_PRICE = 0.5;

/**
 * + PostgreSQL-tietokanta (1 kpl/kk)
 */
export const EXTRA_WEB_HOTEL_PSQL_1_MONTHLY_PRICE = 0.5;


/**
 * + 10 GB
 */
export const EXTRA_EMAIL_DISK_10_MONTHLY_PRICE = Math.ceil( 0.22 * 10 * (1+DEFAULT_VAT_PERCENT) );

/**
 * + 50 GB
 */
export const EXTRA_EMAIL_DISK_50_MONTHLY_PRICE = Math.ceil( 0.22 * 50 * (1+DEFAULT_VAT_PERCENT) );

/**
 * + Ylläpito ja tuki, 1 tunti/kk
 */
export const EXTRA_EMAIL_SUPPORT_1_MONTHLY_PRICE = SUPPORT_HOURLY_FEE / 8;

/**
 * Varmuuskopioiden palautus, 1 tunti/kk
 */
export const EXTRA_EMAIL_BACKUP_RESTORE_1_MONTHLY_PRICE = SUPPORT_HOURLY_FEE / 10;




/**
 * Titan 1 GB, RAID-1-peilaus, 7K SAS 12 Gbps, 1 Gbps, 256 GB/kk
 */
export const WEB_TITAN_1G_MONHTLY_PRICE = 5; // Zoner 4.8945 €/kk/0.5 GB ...tai... Zoner 11.1695 €/kk/10 GB

/**
 * Titan 2 GB, RAID-1-peilaus, 7K SAS 12 Gbps, 1 Gbps, 512 GB/kk
 */
export const WEB_TITAN_2G_MONHTLY_PRICE = 7.9; // Zoner 4.8945 €/kk/0.5 GB ...tai... Zoner 11.1695 €/kk/10 GB

/**
 * Titan 5 GB, RAID-1-peilaus, 7K SAS 12 Gbps, 1 Gbps, 1 TB/kk
 */
export const WEB_TITAN_5G_MONHTLY_PRICE = 9.9; // Zoner 4.8945 €/kk/0.5 GB ...tai... Zoner 11.1695 €/kk/10 GB

/**
 * Titan 10 GB, RAID-1-peilaus, 7K SAS 12 Gbps, 1 Gbps, 1 TB/kk
 */
export const WEB_TITAN_10G_MONHTLY_PRICE = 11.1; // Zoner 11.1695 €/kk/10 GB

/**
 * Titan 20 GB, RAID-1-peilaus, 7K SAS 12 Gbps, 1 Gbps, 1 TB/kk
 */
export const WEB_TITAN_20G_MONHTLY_PRICE = 17.5; // Zoner 17.444499999999998 €/kk/20 GB




/**
 * Atlas 18 GB
 * @see https://github.com/sendanor/project-sendanor.fi/issues/129
 */
export const ATLAS_18G_MONTHLY_PRICE = 60;

/**
 * Atlas 1 GB, RAID-1-peilaus, NVMe M2 SSD, 10 Gbps, 2 TB/kk
 */
export const WEB_ATLAS_1G_MONHTLY_PRICE = 7;

/**
 * Atlas 2 GB, RAID-1-peilaus, NVMe M2 SSD, 10 Gbps, 5 TB/kk
 */
export const WEB_ATLAS_2G_MONHTLY_PRICE = 14;

/**
 * Atlas 5 GB, RAID-1-peilaus, NVMe M2 SSD, 10 Gbps, 10 TB/kk
 */
export const WEB_ATLAS_5G_MONHTLY_PRICE = 35;

/**
 * Atlas 10 GB, RAID-1-peilaus, NVMe M2 SSD, 10 Gbps, 10 TB/kk
 */
export const WEB_ATLAS_10G_MONHTLY_PRICE = 70;

/**
 * Atlas 20 GB, RAID-1-peilaus, NVMe M2 SSD, 10 Gbps, 10 TB/kk
 */
export const WEB_ATLAS_20G_MONHTLY_PRICE = 140;


/**
 * 1 GB & 2 laatikkoa
 */
export const EMAIL_MINI_MONHTLY_PRICE = 1.9; // Zoner 4.8945 €/kk/0.5 GB

/**
 * 10 GB & 10 laatikkoa
 */
export const EMAIL_1_MONHTLY_PRICE = 7; // Zoner 11.1695 €/kk/10 GB

/**
 * 25 GB & 25 laatikkoa
 */
export const EMAIL_2_MONHTLY_PRICE = 14.5; // Zoner 17.444499999999998 €/kk/20 GB

/**
 * 50 GB & 50 laatikkoa
 */
export const EMAIL_3_MONHTLY_PRICE = 31.8; // Zoner 28.739499999999996 €/kk/30 GB



/**
 * GoVM virtual manager, binary release
 */
export const SOFTWARE_GOVM_1_MONHTLY_PRICE = 1255;

/**
 * GoVM virtual manager, GPL-licensed source code release
 */
export const SOFTWARE_GOVM_2_MONHTLY_PRICE = 4999/10;

/**
 * 1 GB
 */
export const SHELL_1_MONHTLY_PRICE = 1;

/**
 * 1 GB
 */
export const SHELL_2_MONHTLY_PRICE = 0.4;


/**
 * Factor from monthly price to yearly price
 */
export const FROM_MONTH_TO_YEAR_FACTOR = 10;

/**
 * Factor from monthly price to 3 year price
 */
export const FROM_MONTH_TO_3_YEAR_FACTOR = 9 * 3;

/**
 * Factor from monthly price to 5 year price
 */
export const FROM_MONTH_TO_5_YEAR_FACTOR = 8 * 5;

/**
 * Factor from monthly price to 10 year price
 */
export const FROM_MONTH_TO_10_YEAR_FACTOR = 7 * 10;

export const WEB_DEFAULT_FEATURES: readonly ProductFeature[] = [
    {
        id : ProductFeatureId.VPS_TYPE,
        category : ProductFeatureCategory.VPS,
        value : VirtualizationType.LXC,
    },
    {
        id : ProductFeatureId.VPS_OS,
        category : ProductFeatureCategory.VPS,
        value : [
            OperatingSystem.LINUX_UBUNTU_LTS_22_04,
            OperatingSystem.LINUX_UBUNTU_LTS_24_04,
        ]
    },
    {
        id : ProductFeatureId.DISK_TYPE,
        category : ProductFeatureCategory.DISK,
        value : DiskType.DISK_HDD,
    },
    {
        id : ProductFeatureId.DISK_RAID,
        category : ProductFeatureCategory.DISK,
        value : RaidType.RAID_MIRROR,
    },
    {
        id : ProductFeatureId.DISK_BACKUP,
        category : ProductFeatureCategory.DISK,
        value : BackupType.INCLUDED_WITH_RESTORE_FEE,
    },
    {
        id : ProductFeatureId.DISK_USAGE,
        category : ProductFeatureCategory.DISK,
        value : DiskUsageType.DATA,
    },
    {
        id : ProductFeatureId.NETWORK_TYPE,
        category : ProductFeatureCategory.NETWORK,
        value : NetworkType.ETH_1G,
    },
    {
        id : ProductFeatureId.NETWORK_ZONE,
        category : ProductFeatureCategory.NETWORK,
        value : NetworkZone.FI_TURKU_1,
    },
    {
        id : ProductFeatureId.NETWORK_IP4,
        category : ProductFeatureCategory.NETWORK,
        value : NetworkIpType.PRIVATE_NAT,
    },
    {
        id : ProductFeatureId.NETWORK_IP6,
        category : ProductFeatureCategory.NETWORK,
        value : NetworkIpType.PRIVATE_NAT,
    },
    {
        id : ProductFeatureId.WEBSERVER_SOFTWARE,
        category : ProductFeatureCategory.WEBSERVER,
        value : [
            WebServer.APACHE,
            WebServer.NGINX,
            WebServer.NODEJS,
            WebServer.OTHER,
        ],
    },
    {
        id : ProductFeatureId.PHP_SOFTWARE,
        category : ProductFeatureCategory.WEBSERVER,
        value : [
            PHPVersion.PHP_7_2,
            PHPVersion.PHP_7_3,
            PHPVersion.PHP_7_4,
            PHPVersion.PHP_8_0,
            PHPVersion.PHP_8_1,
            PHPVersion.PHP_8_2,
            PHPVersion.PHP_8_3,
        ],
    },
    {
        id : ProductFeatureId.PHP_MEMORY,
        category : ProductFeatureCategory.WEBSERVER,
        value : 256,
    },
    {
        id : ProductFeatureId.SSL_CERTIFICATES,
        category : ProductFeatureCategory.WEBSERVER,
        value : true,
    },
    {
        id : ProductFeatureId.SFTP_ACCOUNT_COUNT,
        category : ProductFeatureCategory.WEBSERVER,
        value : 1,
    },
    {
        id : ProductFeatureId.SSH_ACCOUNT_COUNT,
        category : ProductFeatureCategory.WEBSERVER,
        value : 1,
    },
    {
        id : ProductFeatureId.HTTP_2_SUPPORT,
        category : ProductFeatureCategory.WEBSERVER,
        value : true,
    },
    {
        id : ProductFeatureId.HTACCESS_SUPPORT,
        category : ProductFeatureCategory.WEBSERVER,
        value : true,
    },
    {
        id : ProductFeatureId.PASSWORD_PROTECTED_WEB_FOLDER_SUPPORT,
        category : ProductFeatureCategory.WEBSERVER,
        value : true,
    },
    {
        id : ProductFeatureId.CUSTOM_ERROR_PAGE_SUPPORT,
        category : ProductFeatureCategory.WEBSERVER,
        value : true,
    },
    {
        id : ProductFeatureId.NODE_JS_SOFTWARE,
        category : ProductFeatureCategory.WEBSERVER,
        value : true,
    },
    {
        id : ProductFeatureId.CGI,
        category : ProductFeatureCategory.WEBSERVER,
        value : true,
    },
    {
        id : ProductFeatureId.PERL_SOFTWARE,
        category : ProductFeatureCategory.WEBSERVER,
        value : true,
    },
    {
        id : ProductFeatureId.PYTHON_SOFTWARE,
        category : ProductFeatureCategory.WEBSERVER,
        value : true,
    },
    {
        id : ProductFeatureId.CRON,
        category : ProductFeatureCategory.WEBSERVER,
        value : true,
    },
];

export const WEB_DEFAULT_DATABASE_FEATURES: readonly ProductFeature[] = [
    {
        id : ProductFeatureId.MYSQL_SUPPORT,
        category : ProductFeatureCategory.DATABASES,
        value : true,
    },
    {
        id : ProductFeatureId.MARIADB_SUPPORT,
        category : ProductFeatureCategory.DATABASES,
        value : true,
    },
    {
        id : ProductFeatureId.POSTGRESQL_SUPPORT,
        category : ProductFeatureCategory.DATABASES,
        value : true,
    },
    {
        id : ProductFeatureId.REDIS_SUPPORT,
        category : ProductFeatureCategory.DATABASES,
        value : false,
    },
    {
        id : ProductFeatureId.MONGODB_SUPPORT,
        category : ProductFeatureCategory.DATABASES,
        value : false,
    },
    {
        id : ProductFeatureId.ELASTICSEARCH_SUPPORT,
        category : ProductFeatureCategory.DATABASES,
        value : false,
    },
    {
        id : ProductFeatureId.PHPMYADMIN_SUPPORT,
        category : ProductFeatureCategory.DATABASES,
        value : true,
    },
];

const DEFAULT_EMAIL_ADMIN_FEATURES: readonly ProductFeature[] = [
    {
        id : ProductFeatureId.API_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : true,
    },
    {
        id : ProductFeatureId.TOTP_2FA_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : true,
    },
    {
        id : ProductFeatureId.OAUTH2_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : true,
    },
    {
        id : ProductFeatureId.QUOTA_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : true,
    },
    {
        id : ProductFeatureId.STATISTICS_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : true,
    },
    {
        id : ProductFeatureId.MONITORING_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : true,
    },
];

const DISABLED_EMAIL_ADMIN_FEATURES: readonly ProductFeature[] = [
    {
        id : ProductFeatureId.ADMIN_USER_COUNT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : 0,
    },
    {
        id : ProductFeatureId.API_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : false,
    },
    {
        id : ProductFeatureId.TOTP_2FA_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : false,
    },
    {
        id : ProductFeatureId.OAUTH2_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : false,
    },
    {
        id : ProductFeatureId.QUOTA_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : false,
    },
    {
        id : ProductFeatureId.STATISTICS_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : false,
    },
    {
        id : ProductFeatureId.MONITORING_SUPPORT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : false,
    },
];

const DEFAULT_EMAIL_FEATURES: readonly ProductFeature[] = [
    {
        id : ProductFeatureId.AUTOREPLY_SUPPORT,
        category : ProductFeatureCategory.EMAILS,
        value : true,
    },
    {
        id : ProductFeatureId.ROUNDCUBE_SOFTWARE,
        category : ProductFeatureCategory.EMAILS,
        value : true,
    },
    {
        id : ProductFeatureId.IMAP_SUPPORT,
        category : ProductFeatureCategory.EMAILS,
        value : true,
    },
    {
        id : ProductFeatureId.POP_SUPPORT,
        category : ProductFeatureCategory.EMAILS,
        value : true,
    },
    {
        id : ProductFeatureId.SMTP_AUTH_SUPPORT,
        category : ProductFeatureCategory.EMAILS,
        value : true,
    },
    {
        id : ProductFeatureId.SPAM_FILTERING,
        category : ProductFeatureCategory.EMAILS,
        value : true,
    },
    {
        id : ProductFeatureId.CLAMAV_SUPPORT,
        category : ProductFeatureCategory.EMAILS,
        value : true,
    },
    {
        id : ProductFeatureId.DKIM_SUPPORT,
        category : ProductFeatureCategory.EMAILS,
        value : true,
    },
    {
        id : ProductFeatureId.SPF_SUPPORT,
        category : ProductFeatureCategory.EMAILS,
        value : true,
    },
    {
        id : ProductFeatureId.SIEVE_FILTERING_SUPPORT,
        category : ProductFeatureCategory.EMAILS,
        value : true,
    },
    {
        id : ProductFeatureId.USER_THROTTLE_RATE,
        category : ProductFeatureCategory.EMAILS,
        value : 300,
    },
    {
        id : ProductFeatureId.DDOS_THROTTLE_RATE,
        category : ProductFeatureCategory.EMAILS,
        value : 5,
    },
    {
        id : ProductFeatureId.DDOS_LESSER_THROTTLE_RATE,
        category : ProductFeatureCategory.EMAILS,
        value : 200,
    },
    {
        id : ProductFeatureId.LOGIN_THROTTLE_RATE,
        category : ProductFeatureCategory.EMAILS,
        value : 10,
    },
    {
        id : ProductFeatureId.PASSWORD_RECOVERY_THROTTLE_RATE,
        category : ProductFeatureCategory.EMAILS,
        value : 12,
    },
    {
        id : ProductFeatureId.PASSWORD_RECOVERY_TOTP_THROTTLE_RATE,
        category : ProductFeatureCategory.EMAILS,
        value : 25,
    },
    {
        id : ProductFeatureId.PASSWORD_RECOVERY_APPLY_THROTTLE_RATE,
        category : ProductFeatureCategory.EMAILS,
        value : 25,
    },
];

const DEFAULT_EMAIL_SERVER_FEATURES: readonly ProductFeature[] = [
    {
        id : ProductFeatureId.DISK_TYPE,
        category : ProductFeatureCategory.DISK,
        value : DiskType.DISK_NVME_M2_SSD,
    },
    {
        id : ProductFeatureId.DISK_RAID,
        category : ProductFeatureCategory.DISK,
        value : RaidType.RAID_MIRROR,
    },
    {
        id : ProductFeatureId.DISK_BACKUP,
        category : ProductFeatureCategory.DISK,
        value : BackupType.INCLUDED_WITH_RESTORE_FEE,
    },
    {
        id : ProductFeatureId.DISK_USAGE,
        category : ProductFeatureCategory.DISK,
        value : DiskUsageType.DATA,
    },
    {
        id : ProductFeatureId.NETWORK_ZONE,
        category : ProductFeatureCategory.NETWORK,
        value : NetworkZone.FI_TURKU_1,
    },
];

const DEFAULT_MINI_EMAIL_FEATURES = [
    {
        id : ProductFeatureId.EMAIL_DOMAIN_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 1,
    },
    {
        id : ProductFeatureId.EMAIL_DOMAIN_ALIAS_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 3,
    },
    {
        id : ProductFeatureId.DOMAIN_QUOTA,
        category : ProductFeatureCategory.EMAILS,
        value : 1,
    },
    {
        id : ProductFeatureId.MAILBOX_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 2,
    },
    {
        id : ProductFeatureId.MAILBOX_ALIAS_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 10,
    },
    {
        id : ProductFeatureId.DOMAIN_MESSAGE_SENDING_LIMIT,
        category : ProductFeatureCategory.EMAILS,
        value : 200,
    },
];

const DEFAULT_EMAIL_1_FEATURES = [
    {
        id : ProductFeatureId.EMAIL_DOMAIN_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 3,
    },
    {
        id : ProductFeatureId.EMAIL_DOMAIN_ALIAS_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 3*3,
    },
    {
        id : ProductFeatureId.DOMAIN_QUOTA,
        category : ProductFeatureCategory.EMAILS,
        value : 10,
    },
    {
        id : ProductFeatureId.MAILBOX_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 10,
    },
    {
        id : ProductFeatureId.ADMIN_USER_COUNT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : 1,
    },
    {
        id : ProductFeatureId.MAILBOX_ALIAS_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 100,
    },
    {
        id : ProductFeatureId.DOMAIN_MESSAGE_SENDING_LIMIT,
        category : ProductFeatureCategory.EMAILS,
        value : 2000,
    },
    {
        id : ProductFeatureId.MAILBOX_QUOTA,
        category : ProductFeatureCategory.EMAILS,
        value : EmailOption.ADMIN_CONFIGURABLE,
    },
    {
        id : ProductFeatureId.MAILBOX_MESSAGE_SENDING_LIMIT,
        category : ProductFeatureCategory.EMAILS,
        value : EmailOption.ADMIN_CONFIGURABLE,
    },
];

const DEFAULT_EMAIL_2_FEATURES = [
    {
        id : ProductFeatureId.EMAIL_DOMAIN_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 6,
    },
    {
        id : ProductFeatureId.EMAIL_DOMAIN_ALIAS_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 6*3,
    },
    {
        id : ProductFeatureId.DOMAIN_QUOTA,
        category : ProductFeatureCategory.EMAILS,
        value : 25,
    },
    {
        id : ProductFeatureId.MAILBOX_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 25,
    },
    {
        id : ProductFeatureId.ADMIN_USER_COUNT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : 3,
    },
    {
        id : ProductFeatureId.MAILBOX_ALIAS_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 2500,
    },
    {
        id : ProductFeatureId.DOMAIN_MESSAGE_SENDING_LIMIT,
        category : ProductFeatureCategory.EMAILS,
        value : 5000,
    },
    {
        id : ProductFeatureId.MAILBOX_QUOTA,
        category : ProductFeatureCategory.EMAILS,
        value : EmailOption.ADMIN_CONFIGURABLE,
    },
    {
        id : ProductFeatureId.MAILBOX_MESSAGE_SENDING_LIMIT,
        category : ProductFeatureCategory.EMAILS,
        value : EmailOption.ADMIN_CONFIGURABLE,
    },
];

const DEFAULT_EMAIL_3_FEATURES = [
    {
        id : ProductFeatureId.EMAIL_DOMAIN_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 12,
    },
    {
        id : ProductFeatureId.EMAIL_DOMAIN_ALIAS_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 12*3,
    },
    {
        id : ProductFeatureId.DOMAIN_QUOTA,
        category : ProductFeatureCategory.EMAILS,
        value : 50,
    },
    {
        id : ProductFeatureId.MAILBOX_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 50,
    },
    {
        id : ProductFeatureId.ADMIN_USER_COUNT,
        category : ProductFeatureCategory.EMAIL_ADMIN,
        value : 10,
    },
    {
        id : ProductFeatureId.MAILBOX_ALIAS_COUNT,
        category : ProductFeatureCategory.EMAILS,
        value : 5000,
    },
    {
        id : ProductFeatureId.DOMAIN_MESSAGE_SENDING_LIMIT,
        category : ProductFeatureCategory.EMAILS,
        value : 10000,
    },
    {
        id : ProductFeatureId.MAILBOX_QUOTA,
        category : ProductFeatureCategory.EMAILS,
        value : EmailOption.ADMIN_CONFIGURABLE,
    },
    {
        id : ProductFeatureId.MAILBOX_MESSAGE_SENDING_LIMIT,
        category : ProductFeatureCategory.EMAILS,
        value : EmailOption.ADMIN_CONFIGURABLE,
    },
];

const DEFAULT_VPS_FEATURES = [
    {
        id : ProductFeatureId.VPS_TYPE,
        category : ProductFeatureCategory.VPS,
        value : VirtualizationType.KVM,
    },
    {
        id : ProductFeatureId.VPS_OS,
        category : ProductFeatureCategory.VPS,
        value : OperatingSystem.LINUX_FREEBSD,
    },

    {
        id : ProductFeatureId.DISK_SIZE,
        category : ProductFeatureCategory.DISK,
        value : 7,
    },
    {
        id : ProductFeatureId.DISK_TYPE,
        category : ProductFeatureCategory.DISK,
        value : DiskType.DISK_NVME_M2_SSD,
    },
    {
        id : ProductFeatureId.DISK_RAID,
        category : ProductFeatureCategory.DISK,
        value : RaidType.RAID_MIRROR,
    },
    {
        id : ProductFeatureId.DISK_BACKUP,
        category : ProductFeatureCategory.DISK,
        value : BackupType.INCLUDED_WITH_RESTORE_FEE,
    },
    {
        id : ProductFeatureId.DISK_USAGE,
        category : ProductFeatureCategory.DISK,
        value : [
            DiskUsageType.OS,
            DiskUsageType.DATA,
        ]
    },

    {
        id : ProductFeatureId.DISK_2_SIZE,
        category : ProductFeatureCategory.DISK_2,
        value : 1000,
    },
    {
        id : ProductFeatureId.DISK_2_TYPE,
        category : ProductFeatureCategory.DISK_2,
        value : DiskType.DISK_HDD,
    },
    {
        id : ProductFeatureId.DISK_2_RAID,
        category : ProductFeatureCategory.DISK_2,
        value : RaidType.RAID_MIRROR,
    },
    {
        id : ProductFeatureId.DISK_2_BACKUP,
        category : ProductFeatureCategory.DISK_2,
        value : BackupType.INCLUDED_WITH_RESTORE_FEE,
    },
    {
        id : ProductFeatureId.DISK_2_USAGE,
        category : ProductFeatureCategory.DISK_2,
        value : DiskUsageType.DATA,
    },

    {
        id : ProductFeatureId.DISK_3_SIZE,
        category : ProductFeatureCategory.DISK_3,
        value : 1,
    },
    {
        id : ProductFeatureId.DISK_3_TYPE,
        category : ProductFeatureCategory.DISK_3,
        value : DiskType.DISK_NVME_M2_SSD,
    },
    {
        id : ProductFeatureId.DISK_3_RAID,
        category : ProductFeatureCategory.DISK_3,
        value : RaidType.RAID_MIRROR,
    },
    {
        id : ProductFeatureId.DISK_3_BACKUP,
        category : ProductFeatureCategory.DISK_3,
        value : BackupType.UNAVAILABLE,
    },
    {
        id : ProductFeatureId.DISK_3_USAGE,
        category : ProductFeatureCategory.DISK_3,
        value : DiskUsageType.SWAP,
    },

    {
        id : ProductFeatureId.NETWORK_TYPE,
        category : ProductFeatureCategory.NETWORK,
        value : NetworkType.ETH_1G,
    },
    {
        id : ProductFeatureId.NETWORK_TRAFFIC,
        category : ProductFeatureCategory.NETWORK,
        value : 10240,
    },
    {
        id : ProductFeatureId.NETWORK_ZONE,
        category : ProductFeatureCategory.NETWORK,
        value : NetworkZone.FI_TURKU_1,
    },
    {
        id : ProductFeatureId.NETWORK_IP4,
        category : ProductFeatureCategory.NETWORK,
        value : [
            NetworkIpType.PUBLIC_AVAILABLE,
            NetworkIpType.PRIVATE_NAT,
        ],
    },
    {
        id : ProductFeatureId.NETWORK_IP6,
        category : ProductFeatureCategory.NETWORK,
        value : NetworkIpType.PUBLIC_INCLUDED,
    },
    {
        id : ProductFeatureId.NETWORK_NET6,
        category : ProductFeatureCategory.NETWORK,
        value : NetworkIpType.PUBLIC_AVAILABLE,
    },
    {
        id : ProductFeatureId.CPU_SHARE,
        category : ProductFeatureCategory.CPU,
        value : CpuShare.CPU_1_10,
    },

];

export const ALL_PRODUCTS : readonly Product[] = [


    {
        stockAmount : 999, stockEnabled : false, onHold : true, published : false,
        id : SendanorProductId.VPS_MINI_1,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_MINI_1_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_MINI_1_DESCRIPTION,
        prices : [
            {
                type : ProductPriceType.MONTHLY,
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_MINI_1_MONHTLY_PRICE,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_MINI_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_MINI_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_MINI_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_MINI_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [

            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.KVM,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_FREEBSD,
            },

            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 7,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : [
                    DiskUsageType.OS,
                    DiskUsageType.DATA,
                ]
            },

            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 50,
            },
            {
                id : ProductFeatureId.DISK_2_TYPE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.DATA,
            },

            {
                id : ProductFeatureId.DISK_3_SIZE,
                category : ProductFeatureCategory.DISK_3,
                value : 1,
            },
            {
                id : ProductFeatureId.DISK_3_TYPE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_3_RAID,
                category : ProductFeatureCategory.DISK_3,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_3_BACKUP,
                category : ProductFeatureCategory.DISK_3,
                value : BackupType.UNAVAILABLE,
            },
            {
                id : ProductFeatureId.DISK_3_USAGE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskUsageType.SWAP,
            },

            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 1,
            },
            {
                id : ProductFeatureId.CPU_SHARE,
                category : ProductFeatureCategory.CPU,
                value : CpuShare.CPU_1_10,
            },

            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 512,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_1G,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : [
                    NetworkIpType.PUBLIC_AVAILABLE,
                    NetworkIpType.PRIVATE_NAT,
                ],
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
            {
                id : ProductFeatureId.NETWORK_NET6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_AVAILABLE,
            },

        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : true, published : false,
        id : SendanorProductId.VPS_1,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_1_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_1_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_1_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [

            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.KVM,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_FREEBSD,
            },

            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 12,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : [
                    DiskUsageType.OS,
                    DiskUsageType.DATA,
                ]
            },

            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 250,
            },
            {
                id : ProductFeatureId.DISK_2_TYPE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.DATA,
            },

            {
                id : ProductFeatureId.DISK_3_SIZE,
                category : ProductFeatureCategory.DISK_3,
                value : 2,
            },
            {
                id : ProductFeatureId.DISK_3_TYPE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_3_RAID,
                category : ProductFeatureCategory.DISK_3,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_3_BACKUP,
                category : ProductFeatureCategory.DISK_3,
                value : BackupType.UNAVAILABLE,
            },
            {
                id : ProductFeatureId.DISK_3_USAGE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskUsageType.SWAP,
            },

            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 2,
            },
            {
                id : ProductFeatureId.CPU_SHARE,
                category : ProductFeatureCategory.CPU,
                value : CpuShare.CPU_1_10,
            },

            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 1024,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_1G,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 5,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : [
                    NetworkIpType.PUBLIC_AVAILABLE,
                    NetworkIpType.PRIVATE_NAT,
                ],
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
            {
                id : ProductFeatureId.NETWORK_NET6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_AVAILABLE,
            },

        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : true, published : false,
        id : SendanorProductId.VPS_6,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_6_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_6_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_6_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_6_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_6_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_6_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_6_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [

            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.KVM,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_FREEBSD,
            },

            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 16,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : [
                    DiskUsageType.OS,
                    DiskUsageType.DATA,
                ]
            },

            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 1000,
            },
            {
                id : ProductFeatureId.DISK_2_TYPE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.DATA,
            },

            {
                id : ProductFeatureId.DISK_3_SIZE,
                category : ProductFeatureCategory.DISK_3,
                value : 2,
            },
            {
                id : ProductFeatureId.DISK_3_TYPE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_3_RAID,
                category : ProductFeatureCategory.DISK_3,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_3_BACKUP,
                category : ProductFeatureCategory.DISK_3,
                value : BackupType.UNAVAILABLE,
            },
            {
                id : ProductFeatureId.DISK_3_USAGE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskUsageType.SWAP,
            },

            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 3,
            },
            {
                id : ProductFeatureId.CPU_SHARE,
                category : ProductFeatureCategory.CPU,
                value : CpuShare.CPU_1_10,
            },

            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 1024 * 2,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_1G,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 10,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : [
                    NetworkIpType.PUBLIC_AVAILABLE,
                    NetworkIpType.PRIVATE_NAT,
                ],
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
            {
                id : ProductFeatureId.NETWORK_NET6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_AVAILABLE,
            },

        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : true, published : false,
        id : SendanorProductId.VPS_7,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_7_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_7_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_7_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_7_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_7_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_7_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_7_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.KVM,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_FREEBSD,
            },
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 24,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : [
                    DiskUsageType.OS,
                    DiskUsageType.DATA,
                ]
            },

            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 2 * 1024,
            },
            {
                id : ProductFeatureId.DISK_2_TYPE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.DATA,
            },

            {
                id : ProductFeatureId.DISK_3_SIZE,
                category : ProductFeatureCategory.DISK_3,
                value : 2 * 2,
            },
            {
                id : ProductFeatureId.DISK_3_TYPE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_3_RAID,
                category : ProductFeatureCategory.DISK_3,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_3_BACKUP,
                category : ProductFeatureCategory.DISK_3,
                value : BackupType.UNAVAILABLE,
            },
            {
                id : ProductFeatureId.DISK_3_USAGE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskUsageType.SWAP,
            },

            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 4,
            },
            {
                id : ProductFeatureId.CPU_SHARE,
                category : ProductFeatureCategory.CPU,
                value : CpuShare.CPU_1_10,
            },
            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 4 * 1024,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_1G,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : [
                    NetworkIpType.PUBLIC_AVAILABLE,
                    NetworkIpType.PRIVATE_NAT,
                ],
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
            {
                id : ProductFeatureId.NETWORK_NET6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_AVAILABLE,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 10 * 2,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },

        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : true, published : false,
        id : SendanorProductId.VPS_8,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_8_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_8_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_8_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_8_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_8_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_8_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_8_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.KVM,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_FREEBSD,
            },
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 32,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : [
                    DiskUsageType.OS,
                    DiskUsageType.DATA,
                ]
            },

            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 4 * 1024,
            },
            {
                id : ProductFeatureId.DISK_2_TYPE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.DATA,
            },

            {
                id : ProductFeatureId.DISK_3_SIZE,
                category : ProductFeatureCategory.DISK_3,
                value : 2 * 4,
            },
            {
                id : ProductFeatureId.DISK_3_TYPE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_3_RAID,
                category : ProductFeatureCategory.DISK_3,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_3_BACKUP,
                category : ProductFeatureCategory.DISK_3,
                value : BackupType.UNAVAILABLE,
            },
            {
                id : ProductFeatureId.DISK_3_USAGE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskUsageType.SWAP,
            },

            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 6,
            },
            {
                id : ProductFeatureId.CPU_SHARE,
                category : ProductFeatureCategory.CPU,
                value : CpuShare.CPU_1_10,
            },
            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 1024 * 8,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_1G,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 10 * 4,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : [
                    NetworkIpType.PUBLIC_AVAILABLE,
                    NetworkIpType.PRIVATE_NAT,
                ],
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
            {
                id : ProductFeatureId.NETWORK_NET6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_AVAILABLE,
            },
        ],
    },

    {
        stockAmount : 0, stockEnabled : false, published : false, onHold : true,
        id : SendanorProductId.VPS_2,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_2_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_2_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 124,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 135,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 150,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 240,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 300,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [

            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.KVM,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_FREEBSD,
            },

            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 8,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : [
                    DiskUsageType.OS,
                    DiskUsageType.DATA,
                ]
            },

            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 1000,
            },
            {
                id : ProductFeatureId.DISK_2_TYPE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.DATA,
            },

            {
                id : ProductFeatureId.DISK_3_SIZE,
                category : ProductFeatureCategory.DISK_3,
                value : 2,
            },
            {
                id : ProductFeatureId.DISK_3_TYPE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_3_RAID,
                category : ProductFeatureCategory.DISK_3,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_3_BACKUP,
                category : ProductFeatureCategory.DISK_3,
                value : BackupType.UNAVAILABLE,
            },
            {
                id : ProductFeatureId.DISK_3_USAGE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskUsageType.SWAP,
            },

            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 1,
            },
            {
                id : ProductFeatureId.CPU_SHARE,
                category : ProductFeatureCategory.CPU,
                value : CpuShare.CPU_1_10,
            },

            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 1024,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_1G,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 10,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : [
                    NetworkIpType.PUBLIC_AVAILABLE,
                    NetworkIpType.PRIVATE_NAT,
                ],
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
            {
                id : ProductFeatureId.NETWORK_NET6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_AVAILABLE,
            },

        ],
    },
    {
        stockAmount : 0, stockEnabled : false, published : false, onHold : true,
        id : SendanorProductId.VPS_3,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_3_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_3_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 243,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 270,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 300,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 300 / 3 * 4.75,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 300 / 3 * 9,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.KVM,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_FREEBSD,
            },
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 8,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : [
                    DiskUsageType.OS,
                    DiskUsageType.DATA,
                ]
            },

            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 2 * 1024,
            },
            {
                id : ProductFeatureId.DISK_2_TYPE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.DATA,
            },

            {
                id : ProductFeatureId.DISK_3_SIZE,
                category : ProductFeatureCategory.DISK_3,
                value : 2,
            },
            {
                id : ProductFeatureId.DISK_3_TYPE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_3_RAID,
                category : ProductFeatureCategory.DISK_3,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_3_BACKUP,
                category : ProductFeatureCategory.DISK_3,
                value : BackupType.UNAVAILABLE,
            },
            {
                id : ProductFeatureId.DISK_3_USAGE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskUsageType.SWAP,
            },

            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 2,
            },
            {
                id : ProductFeatureId.CPU_SHARE,
                category : ProductFeatureCategory.CPU,
                value : CpuShare.CPU_1_10,
            },
            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 2 * 1024,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_1G,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : [
                    NetworkIpType.PUBLIC_AVAILABLE,
                    NetworkIpType.PRIVATE_NAT,
                ],
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
            {
                id : ProductFeatureId.NETWORK_NET6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_AVAILABLE,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 10 * 2,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },

        ],
    },
    {
        stockAmount : 0, stockEnabled : false, published : false, onHold : true,
        id : SendanorProductId.VPS_4,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_4_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_4_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 486,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 540,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 600,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 600 / 3 * 4.75,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 600 / 3 * 9,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.KVM,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_FREEBSD,
            },
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 8,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : [
                    DiskUsageType.OS,
                    DiskUsageType.DATA,
                ]
            },

            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 4 * 1024,
            },
            {
                id : ProductFeatureId.DISK_2_TYPE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : [
                    DiskUsageType.OS,
                    DiskUsageType.DATA,
                ]
            },

            {
                id : ProductFeatureId.DISK_3_SIZE,
                category : ProductFeatureCategory.DISK_3,
                value : 2,
            },
            {
                id : ProductFeatureId.DISK_3_TYPE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_3_RAID,
                category : ProductFeatureCategory.DISK_3,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_3_BACKUP,
                category : ProductFeatureCategory.DISK_3,
                value : BackupType.UNAVAILABLE,
            },
            {
                id : ProductFeatureId.DISK_3_USAGE,
                category : ProductFeatureCategory.DISK_3,
                value : DiskUsageType.SWAP,
            },

            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 4,
            },
            {
                id : ProductFeatureId.CPU_SHARE,
                category : ProductFeatureCategory.CPU,
                value : CpuShare.CPU_1_10,
            },
            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 1024 * 4,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_1G,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 10 * 4,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : [
                    NetworkIpType.PUBLIC_AVAILABLE,
                    NetworkIpType.PRIVATE_NAT,
                ],
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
            {
                id : ProductFeatureId.NETWORK_NET6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_AVAILABLE,
            },
        ],
    },

    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_10,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_10_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_10_DESCRIPTION,
        prices : [
            {
                type : ProductPriceType.MONTHLY,
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_10_MONHTLY_PRICE,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_10_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_10_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_10_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_10_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            ...DEFAULT_VPS_FEATURES,
            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 1,
            },
            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 512,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_11,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_11_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_11_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_11_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_11_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_11_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_11_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_11_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            ...DEFAULT_VPS_FEATURES,
            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 2,
            },
            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 1024,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_12,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_12_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_12_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_12_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_12_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_12_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_12_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_12_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            ...DEFAULT_VPS_FEATURES,
            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 4,
            },
            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 1024*2,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_13,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_13_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_13_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_13_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_13_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_13_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_13_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_13_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            ...DEFAULT_VPS_FEATURES,
            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 8,
            },
            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 1024*4,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_14,
        type : ProductType.VIRTUAL_SERVER,
        title : T_VIRTUALSERVERS_PRODUCT_14_TITLE,
        summary : T_VIRTUALSERVERS_PRODUCT_14_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_14_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_14_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_14_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_14_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_14_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            ...DEFAULT_VPS_FEATURES,
            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 16,
            },
            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 1024*8,
            },
        ],
    },


    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_TITAN_1G,
        type : ProductType.WEB_HOTEL,
        title : T_WEBHOTELS_PRODUCT_TITAN_1G_TITLE,
        summary : T_WEBHOTELS_PRODUCT_TITAN_1G_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_1G_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_1G_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_1G_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_1G_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_1G_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : margeFeatures(
            SendanorProductId.WEB_TITAN_1G,
            [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 1,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 / 4,
            },
            {
                id : ProductFeatureId.DOMAIN_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 5,
            },
            {
                id : ProductFeatureId.DOMAIN_ALIAS_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 9,
            },
            {
                id : ProductFeatureId.SUB_DOMAIN_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 9,
            },
            {
                id : ProductFeatureId.DATABASE_COUNT,
                category : ProductFeatureCategory.DATABASES,
                value : 1,
            },
                ],
            WEB_DEFAULT_FEATURES,
            WEB_DEFAULT_DATABASE_FEATURES,
            [
                {
                    id : ProductFeatureId.MAILBOX_QUOTA,
                    category : ProductFeatureCategory.EMAILS,
                    value : false,
                },
                {
                    id : ProductFeatureId.MAILBOX_MESSAGE_SENDING_LIMIT,
                    category : ProductFeatureCategory.EMAILS,
                    value : 100,
                },
            ],
            DEFAULT_MINI_EMAIL_FEATURES,
            DEFAULT_EMAIL_FEATURES,
            DISABLED_EMAIL_ADMIN_FEATURES,
        ),
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_TITAN_2G,
        type : ProductType.WEB_HOTEL,
        title : T_WEBHOTELS_PRODUCT_TITAN_2G_TITLE,
        summary : T_WEBHOTELS_PRODUCT_TITAN_2G_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_2G_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_2G_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_2G_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_2G_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_2G_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : margeFeatures(
                SendanorProductId.WEB_TITAN_2G,
                [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 2,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 / 2,
            },
            {
                id : ProductFeatureId.DOMAIN_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 10,
            },
            {
                id : ProductFeatureId.DOMAIN_ALIAS_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 99,
            },
            {
                id : ProductFeatureId.SUB_DOMAIN_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 99,
            },
            {
                id : ProductFeatureId.DATABASE_COUNT,
                category : ProductFeatureCategory.DATABASES,
                value : 1,
            },
                    ],
            WEB_DEFAULT_FEATURES,
            WEB_DEFAULT_DATABASE_FEATURES,
                [
            {
                id : ProductFeatureId.MAILBOX_QUOTA,
                category : ProductFeatureCategory.EMAILS,
                value : false,
            },
            {
                id : ProductFeatureId.MAILBOX_MESSAGE_SENDING_LIMIT,
                category : ProductFeatureCategory.EMAILS,
                value : 100,
            },
                    ],
            DEFAULT_MINI_EMAIL_FEATURES,
            DEFAULT_EMAIL_FEATURES,
            DISABLED_EMAIL_ADMIN_FEATURES,
        ),
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_TITAN_5G,
        type : ProductType.WEB_HOTEL,
        title : T_WEBHOTELS_PRODUCT_TITAN_5G_TITLE,
        summary : T_WEBHOTELS_PRODUCT_TITAN_5G_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_5G_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_5G_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_5G_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_5G_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_5G_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 5,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024,
            },
            {
                id : ProductFeatureId.DOMAIN_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 15,
            },
            {
                id : ProductFeatureId.DOMAIN_ALIAS_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 499,
            },
            {
                id : ProductFeatureId.SUB_DOMAIN_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 499,
            },
            {
                id : ProductFeatureId.DATABASE_COUNT,
                category : ProductFeatureCategory.DATABASES,
                value : 2,
            },
            ...WEB_DEFAULT_FEATURES,
            ...WEB_DEFAULT_DATABASE_FEATURES,
            {
                id : ProductFeatureId.MAILBOX_QUOTA,
                category : ProductFeatureCategory.EMAILS,
                value : false,
            },
            {
                id : ProductFeatureId.MAILBOX_MESSAGE_SENDING_LIMIT,
                category : ProductFeatureCategory.EMAILS,
                value : 100,
            },
            ...DEFAULT_MINI_EMAIL_FEATURES,
            ...DEFAULT_EMAIL_FEATURES,
            ...DISABLED_EMAIL_ADMIN_FEATURES,
        ],
    },
    {
        stockAmount : 0, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_TITAN_10G,
        type : ProductType.WEB_HOTEL,
        title : T_WEBHOTELS_PRODUCT_TITAN_10G_TITLE,
        summary : T_WEBHOTELS_PRODUCT_TITAN_10G_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_10G_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_10G_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_10G_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_10G_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_10G_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : margeFeatures(
            SendanorProductId.WEB_TITAN_10G,
            [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 10,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 2,
            },
            {
                id : ProductFeatureId.DOMAIN_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 30,
            },
            {
                id : ProductFeatureId.DOMAIN_ALIAS_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 999,
            },
            {
                id : ProductFeatureId.SUB_DOMAIN_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 999,
            },
            {
                id : ProductFeatureId.DATABASE_COUNT,
                category : ProductFeatureCategory.DATABASES,
                value : 3,
            }
            ],
            WEB_DEFAULT_FEATURES,
            WEB_DEFAULT_DATABASE_FEATURES,
            DEFAULT_EMAIL_1_FEATURES,
            DEFAULT_EMAIL_FEATURES,
            DEFAULT_EMAIL_ADMIN_FEATURES,
        ),
    },
    {
        stockAmount : 0, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_TITAN_20G,
        type : ProductType.WEB_HOTEL,
        title : T_WEBHOTELS_PRODUCT_TITAN_20G_TITLE,
        summary : T_WEBHOTELS_PRODUCT_TITAN_20G_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_20G_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_20G_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_20G_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_20G_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_TITAN_20G_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : margeFeatures(
            SendanorProductId.WEB_TITAN_20G,
            [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 20,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 4,
            },
            {
                id : ProductFeatureId.DOMAIN_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 60,
            },
            {
                id : ProductFeatureId.DOMAIN_ALIAS_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 9999,
            },
            {
                id : ProductFeatureId.SUB_DOMAIN_COUNT,
                category : ProductFeatureCategory.DOMAINS,
                value : 9999,
            },
            {
                id : ProductFeatureId.DATABASE_COUNT,
                category : ProductFeatureCategory.DATABASES,
                value : 99,
            }],
            WEB_DEFAULT_FEATURES,
            WEB_DEFAULT_DATABASE_FEATURES,
            DEFAULT_EMAIL_2_FEATURES,
            DEFAULT_EMAIL_FEATURES,
            DEFAULT_EMAIL_ADMIN_FEATURES,
        ),
    },




    {
        stockAmount : 0, stockEnabled : false, published : false, onHold : true,
        id : SendanorProductId.WEB_ATLAS_2G,
        type : ProductType.WEB_HOTEL,
        title : T_WEBHOTELS_PRODUCT_ATLAS_2G_TITLE,
        summary : T_WEBHOTELS_PRODUCT_ATLAS_2G_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_2G_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_2G_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_2G_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_2G_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_2G_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.LXC,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_UBUNTU_LTS_22_04,
            },
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 2,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_RESTORE_FREE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : DiskUsageType.DATA,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_10G,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 5,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PRIVATE_NAT,
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PRIVATE_NAT,
            },
        ],
    },
    {
        stockAmount : 0, stockEnabled : false, published : false, onHold : true,
        id : SendanorProductId.WEB_ATLAS_5G,
        type : ProductType.WEB_HOTEL,
        title : T_WEBHOTELS_PRODUCT_ATLAS_5G_TITLE,
        summary : T_WEBHOTELS_PRODUCT_ATLAS_5G_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_5G_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_5G_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_5G_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_5G_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_5G_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.LXC,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_UBUNTU_LTS_22_04,
            },
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 5,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_RESTORE_FREE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : DiskUsageType.DATA,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_10G,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 10,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PRIVATE_NAT,
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PRIVATE_NAT,
            },
        ],
    },
    {
        stockAmount : 0, stockEnabled : false, published : false, onHold : true,
        id : SendanorProductId.WEB_ATLAS_10G,
        type : ProductType.WEB_HOTEL,
        title : T_WEBHOTELS_PRODUCT_ATLAS_10G_TITLE,
        summary : T_WEBHOTELS_PRODUCT_ATLAS_10G_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_10G_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                // discountPercent: WEB_DISCOUNT_PERCENT,
                // discountFrom: WEB_DISCOUNT_FROM,
                // discountTo: WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_10G_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_10G_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_10G_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_10G_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.LXC,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_UBUNTU_LTS_22_04,
            },
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 10,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : DiskUsageType.DATA,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_10G,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 10 * 2,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PRIVATE_NAT,
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PRIVATE_NAT,
            },
        ],
    },
    {
        stockAmount : 0, stockEnabled : false, published : false, onHold : true,
        id : SendanorProductId.WEB_ATLAS_20G,
        type : ProductType.WEB_HOTEL,
        title : T_WEBHOTELS_PRODUCT_ATLAS_20G_TITLE,
        summary : T_WEBHOTELS_PRODUCT_ATLAS_20G_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_20G_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                // discountPercent: WEB_DISCOUNT_PERCENT,
                // discountFrom: WEB_DISCOUNT_FROM,
                // discountTo: WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_20G_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_20G_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_20G_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEB_ATLAS_20G_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.LXC,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_UBUNTU_LTS_22_04,
            },
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 20,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : DiskUsageType.DATA,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_10G,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 10 * 4,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PRIVATE_NAT,
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PRIVATE_NAT,
            },
        ],
    },


    {
        order: 1,
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.EMAIL_MIMI,
        type : ProductType.EMAIL,
        title : T_EMAIL_PRODUCT_MINI_TITLE,
        summary : T_EMAIL_PRODUCT_MINI_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EMAIL_MINI_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EMAIL_MINI_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EMAIL_MINI_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EMAIL_MINI_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EMAIL_MINI_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 1,
            },
            {
                id : ProductFeatureId.ADMIN_USER_COUNT,
                category : ProductFeatureCategory.EMAIL_ADMIN,
                value : 1,
            },
            {
                id : ProductFeatureId.MAILBOX_QUOTA,
                category : ProductFeatureCategory.EMAILS,
                value : EmailOption.ADMIN_CONFIGURABLE,
            },
            {
                id : ProductFeatureId.MAILBOX_MESSAGE_SENDING_LIMIT,
                category : ProductFeatureCategory.EMAILS,
                value : EmailOption.ADMIN_CONFIGURABLE,
            },
            ...DEFAULT_MINI_EMAIL_FEATURES,
            ...DEFAULT_EMAIL_FEATURES,
            ...DEFAULT_EMAIL_SERVER_FEATURES,
            ...DEFAULT_EMAIL_ADMIN_FEATURES,
        ],
    },
    {
        order: 2,
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.EMAIL_1,
        type : ProductType.EMAIL,
        title : T_EMAIL_PRODUCT_1_TITLE,
        summary : T_EMAIL_PRODUCT_1_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EMAIL_1_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EMAIL_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EMAIL_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EMAIL_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EMAIL_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 10,
            },
            ...DEFAULT_EMAIL_1_FEATURES,
            ...DEFAULT_EMAIL_FEATURES,
            ...DEFAULT_EMAIL_SERVER_FEATURES,
            ...DEFAULT_EMAIL_ADMIN_FEATURES,
        ],
    },
    {
        order: 3,
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.EMAIL_2,
        type : ProductType.EMAIL,
        title : T_EMAIL_PRODUCT_2_TITLE,
        summary : T_EMAIL_PRODUCT_2_DESCRIPTION,
        prices : [
            {
                sum : EMAIL_2_MONHTLY_PRICE,
                vatPercent : DEFAULT_VAT_PERCENT,
                type : ProductPriceType.MONTHLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                sum : EMAIL_2_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                vatPercent : DEFAULT_VAT_PERCENT,
                type : ProductPriceType.YEARLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                sum : EMAIL_2_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                vatPercent : DEFAULT_VAT_PERCENT,
                type : ProductPriceType.YEARS_3,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                sum : EMAIL_2_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                vatPercent : DEFAULT_VAT_PERCENT,
                type : ProductPriceType.YEARS_5,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                sum : EMAIL_2_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                vatPercent : DEFAULT_VAT_PERCENT,
                type : ProductPriceType.YEARS_10,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 25,
            },
            ...DEFAULT_EMAIL_2_FEATURES,
            ...DEFAULT_EMAIL_FEATURES,
            ...DEFAULT_EMAIL_SERVER_FEATURES,
            ...DEFAULT_EMAIL_ADMIN_FEATURES,
        ],
    },
    {
        order: 4,
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.EMAIL_3,
        type : ProductType.EMAIL,
        title : T_EMAIL_PRODUCT_3_TITLE,
        summary : T_EMAIL_PRODUCT_3_DESCRIPTION,
        prices : [
            {
                sum : EMAIL_3_MONHTLY_PRICE,
                vatPercent : DEFAULT_VAT_PERCENT,
                type : ProductPriceType.MONTHLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                sum : EMAIL_3_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                vatPercent : DEFAULT_VAT_PERCENT,
                type : ProductPriceType.YEARLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                sum : EMAIL_3_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                vatPercent : DEFAULT_VAT_PERCENT,
                type : ProductPriceType.YEARS_3,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                sum : EMAIL_3_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                vatPercent : DEFAULT_VAT_PERCENT,
                type : ProductPriceType.YEARS_5,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                sum : EMAIL_3_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                vatPercent : DEFAULT_VAT_PERCENT,
                type : ProductPriceType.YEARS_10,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 50,
            },
            ...DEFAULT_EMAIL_3_FEATURES,
            ...DEFAULT_EMAIL_FEATURES,
            ...DEFAULT_EMAIL_SERVER_FEATURES,
            ...DEFAULT_EMAIL_ADMIN_FEATURES,
        ],
    },


    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.SOFTWARE_GOVM_BIN_1,
        type : ProductType.SOFTWARE,
        title : T_SOFTWARE_GOVM_PRODUCT_1_TITLE,
        summary : T_SOFTWARE_GOVM_PRODUCT_1_SUMMARY,
        prices : [
            // {
            //     vatPercent : DEFAULT_VAT_PERCENT,
            //     sum : SOFTWARE_GOVM_1_MONHTLY_PRICE,
            //     type : ProductPriceType.MONTHLY,
            //     discountPercent : SOFTWARE_DISCOUNT_PERCENT,
            //     discountFrom : SOFTWARE_DISCOUNT_FROM,
            //     discountTo : SOFTWARE_DISCOUNT_TO,
            //     published : true,
            // },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SOFTWARE_GOVM_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : SOFTWARE_DISCOUNT_PERCENT,
                discountFrom : SOFTWARE_DISCOUNT_FROM,
                discountTo : SOFTWARE_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SOFTWARE_GOVM_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : SOFTWARE_DISCOUNT_PERCENT,
                discountFrom : SOFTWARE_DISCOUNT_FROM,
                discountTo : SOFTWARE_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SOFTWARE_GOVM_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : SOFTWARE_DISCOUNT_PERCENT,
                discountFrom : SOFTWARE_DISCOUNT_FROM,
                discountTo : SOFTWARE_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SOFTWARE_GOVM_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : SOFTWARE_DISCOUNT_PERCENT,
                discountFrom : SOFTWARE_DISCOUNT_FROM,
                discountTo : SOFTWARE_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.SOFTWARE_GOVM_FSL_2,
        type : ProductType.SOFTWARE,
        title : T_SOFTWARE_GOVM_PRODUCT_2_TITLE,
        summary : T_SOFTWARE_GOVM_PRODUCT_2_SUMMARY,
        prices : [
            // {
            //     vatPercent : DEFAULT_VAT_PERCENT,
            //     sum : SOFTWARE_GOVM_2_MONHTLY_PRICE,
            //     type : ProductPriceType.MONTHLY,
            //     discountPercent : SOFTWARE_DISCOUNT_PERCENT,
            //     discountFrom : SOFTWARE_DISCOUNT_FROM,
            //     discountTo : SOFTWARE_DISCOUNT_TO,
            //     published : true,
            // },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SOFTWARE_GOVM_2_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : SOFTWARE_DISCOUNT_PERCENT,
                discountFrom : SOFTWARE_DISCOUNT_FROM,
                discountTo : SOFTWARE_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SOFTWARE_GOVM_2_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : SOFTWARE_DISCOUNT_PERCENT,
                discountFrom : SOFTWARE_DISCOUNT_FROM,
                discountTo : SOFTWARE_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SOFTWARE_GOVM_2_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : SOFTWARE_DISCOUNT_PERCENT,
                discountFrom : SOFTWARE_DISCOUNT_FROM,
                discountTo : SOFTWARE_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SOFTWARE_GOVM_2_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : SOFTWARE_DISCOUNT_PERCENT,
                discountFrom : SOFTWARE_DISCOUNT_FROM,
                discountTo : SOFTWARE_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
        ],
    },


    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.SHELL_1,
        type : ProductType.SHELL,
        title : T_SHELL_PRODUCT_1_TITLE,
        summary : T_SHELL_PRODUCT_1_SUMMARY,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SHELL_1_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : SHELL_DISCOUNT_PERCENT,
                discountFrom : SHELL_DISCOUNT_FROM,
                discountTo : SHELL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SHELL_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : SHELL_DISCOUNT_PERCENT,
                discountFrom : SHELL_DISCOUNT_FROM,
                discountTo : SHELL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SHELL_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : SHELL_DISCOUNT_PERCENT,
                discountFrom : SHELL_DISCOUNT_FROM,
                discountTo : SHELL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SHELL_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : SHELL_DISCOUNT_PERCENT,
                discountFrom : SHELL_DISCOUNT_FROM,
                discountTo : SHELL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SHELL_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : SHELL_DISCOUNT_PERCENT,
                discountFrom : SHELL_DISCOUNT_FROM,
                discountTo : SHELL_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 10,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : DiskUsageType.DATA,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_OULU_1,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.SHELL_2,
        type : ProductType.SHELL,
        title : T_SHELL_PRODUCT_2_TITLE,
        summary : T_SHELL_PRODUCT_2_SUMMARY,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SHELL_2_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : SHELL_DISCOUNT_PERCENT,
                discountFrom : SHELL_DISCOUNT_FROM,
                discountTo : SHELL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SHELL_2_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : SHELL_DISCOUNT_PERCENT,
                discountFrom : SHELL_DISCOUNT_FROM,
                discountTo : SHELL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SHELL_2_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : SHELL_DISCOUNT_PERCENT,
                discountFrom : SHELL_DISCOUNT_FROM,
                discountTo : SHELL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SHELL_2_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : SHELL_DISCOUNT_PERCENT,
                discountFrom : SHELL_DISCOUNT_FROM,
                discountTo : SHELL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : SHELL_2_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : SHELL_DISCOUNT_PERCENT,
                discountFrom : SHELL_DISCOUNT_FROM,
                discountTo : SHELL_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 10,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : DiskUsageType.DATA,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_OULU_1,
            },
        ],
    },


    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_CPU_1_10,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_CPU_1_10 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_CPU_1_10 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_CPU_1_10_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_CPU_1_10_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_CPU_1_10_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_CPU_1_10_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_CPU_1_10_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 1,
            },
            {
                id : ProductFeatureId.CPU_SHARE,
                category : ProductFeatureCategory.CPU,
                value : CpuShare.CPU_1_10,
            },
        ],
    },
    {
        stockAmount : 0, stockEnabled : false, published : false, onHold : true,
        id : SendanorProductId.VPS_CPU_1_5,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_CPU_1_5 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_CPU_1_5 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 160 / 2,
                type : ProductPriceType.YEARS_3,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : 160,
                type : ProductPriceType.YEARS_10,
            },
        ],
        features : [
            {
                id : ProductFeatureId.CPU_AMOUNT,
                category : ProductFeatureCategory.CPU,
                value : 1,
            },
            {
                id : ProductFeatureId.CPU_SHARE,
                category : ProductFeatureCategory.CPU,
                value : CpuShare.CPU_1_5,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_MEMORY_MINI_1,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_MEMORY_MINI_1 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_MEMORY_MINI_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_MEMORY_MINI_1_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_MEMORY_MINI_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_MEMORY_MINI_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_MEMORY_MINI_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_MEMORY_MINI_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 512,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_MEMORY_1,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_MEMORY_1 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_MEMORY_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_MEMORY_1_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_MEMORY_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_MEMORY_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_MEMORY_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_MEMORY_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.MEMORY_SIZE,
                category : ProductFeatureCategory.MEMORY,
                value : 1024,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_NET6_1,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_NET6_1 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_NET6_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_NET6_1_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_NET6_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_NET6_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_NET6_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_NET6_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.NETWORK_NET6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_IP4_1,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_IP4_1 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_IP4_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_IP4_1_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_IP4_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_IP4_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_IP4_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_IP4_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_ETH2G_1,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_ETH2G_1 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_ETH2G_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH2G_1_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH2G_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH2G_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH2G_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH2G_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_2G,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_ETH5G_1,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_ETH5G_1 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_ETH5G_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH5G_1_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH5G_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH5G_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH5G_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH5G_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_5G,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_ETH10G_1,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_ETH10G_1 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_ETH10G_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH10G_1_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH10G_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH10G_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH10G_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_ETH10G_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_10G,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_SAS_250,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_SAS_250 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_SAS_250 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_250_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_250_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_250_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_250_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_250_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 250,
            },
            {
                id : ProductFeatureId.DISK_2_TYPE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.ANY,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_SAS_500,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_SAS_500 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_SAS_500 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_500_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_500_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_500_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_500_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_500_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 500,
            },
            {
                id : ProductFeatureId.DISK_2_TYPE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.ANY,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_SAS_1000,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_SAS_1000 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_SAS_1000 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_1000_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_1000_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_1000_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_1000_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SAS_1000_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 1000,
            },
            {
                id : ProductFeatureId.DISK_2_TYPE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskType.DISK_HDD,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.ANY,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_NVME_10,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_NVME_10 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_NVME_10 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_NVME_10_MONHTLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_NVME_10_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_NVME_10_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_NVME_10_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_NVME_10_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 10,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : [
                    DiskUsageType.OS,
                    DiskUsageType.DATA,
                    DiskUsageType.ANY,
                ]
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_SUPPORT_1,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_SUPPORT_1 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_SUPPORT_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SUPPORT_1_MONHTLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SUPPORT_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 967.20, // 65 €/h
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SUPPORT_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1800*(1+DEFAULT_VAT_PERCENT), // 50 €/h
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SUPPORT_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2700*(1+DEFAULT_VAT_PERCENT), // 45 €/h
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_SUPPORT_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 5160*(1+DEFAULT_VAT_PERCENT), // 43 €/h
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.SUPPORT_1H_M,
                category : ProductFeatureCategory.SUPPORT,
                value : 1,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_UPGRADE_1,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_UPGRADE_1 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_UPGRADE_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_UPGRADE_1_MONHTLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_UPGRADE_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 967.20, // 65 €/h
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_UPGRADE_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1800*(1+DEFAULT_VAT_PERCENT), // 50 €/h
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_UPGRADE_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2700*(1+DEFAULT_VAT_PERCENT), // 45 €/h
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_UPGRADE_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 5160*(1+DEFAULT_VAT_PERCENT), // 43 €/h
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.UPGRADE_1VPS_M,
                category : ProductFeatureCategory.UPGRADE,
                value : 1,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.VPS_BACKUP_RESTORE_1,
        type : ProductType.VIRTUAL_SERVER_EXTRA,
        title : T_VIRTUAL_SERVER_EXTRA_TITLE( SendanorProductId.VPS_BACKUP_RESTORE_1 ),
        summary : T_VIRTUAL_SERVER_EXTRA_SUMMARY( SendanorProductId.VPS_BACKUP_RESTORE_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_BACKUP_RESTORE_1_MONHTLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_BACKUP_RESTORE_1_MONHTLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 520.80, // 1 x 6 x 70 €/h + ALV
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_BACKUP_RESTORE_1_MONHTLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1450.80, // 3 x 6 x 65 €/h + ALV
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_BACKUP_RESTORE_1_MONHTLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2232, // 5 x 6 x 60 €/h + ALV
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : VPS_EXTRA_VPS_BACKUP_RESTORE_1_MONHTLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 4092, // 10 x 6 x 55 €/h + ALV
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.BACKUP_RESTORE_1H_M,
                category : ProductFeatureCategory.SUPPORT,
                value : true,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : false,
        id : SendanorProductId.WP_1,
        type : ProductType.WP,
        title : T_WEB_EXTRA_TITLE( SendanorProductId.WP_1 ),
        summary : T_WEB_EXTRA_SUMMARY( SendanorProductId.WP_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WP_1_HOURLY_PRICE,
                // sum: 75*(1+DEFAULT_VAT_PERCENT),
                type : ProductPriceType.HOURLY,
                discountPercent : WP_DISCOUNT_PERCENT,
                discountFrom : WP_DISCOUNT_FROM,
                discountTo : WP_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WP_1_MONTHLY_PRICE,
                // sum: 900,
                type : ProductPriceType.MONTHLY,
                discountPercent : WP_DISCOUNT_PERCENT,
                discountFrom : WP_DISCOUNT_FROM,
                discountTo : WP_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WP_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : WP_DISCOUNT_PERCENT,
                discountFrom : WP_DISCOUNT_FROM,
                discountTo : WP_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WP_1_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1116,
                type : ProductPriceType.YEARS_3,
                discountPercent : WP_DISCOUNT_PERCENT,
                discountFrom : WP_DISCOUNT_FROM,
                discountTo : WP_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WP_1_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 1674,
                type : ProductPriceType.YEARS_5,
                discountPercent : WP_DISCOUNT_PERCENT,
                discountFrom : WP_DISCOUNT_FROM,
                discountTo : WP_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WP_1_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 50*(1+DEFAULT_VAT_PERCENT)*0.5*36 / 3 * 7.5,
                type : ProductPriceType.YEARS_10,
                discountPercent : WP_DISCOUNT_PERCENT,
                discountFrom : WP_DISCOUNT_FROM,
                discountTo : WP_DISCOUNT_TO,
                published: true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.WP,
                category : ProductFeatureCategory.SUPPORT,
                value : true,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : false,
        id : SendanorProductId.NET_1,
        type : ProductType.NET,
        title : T_NET_EXTRA_TITLE( SendanorProductId.NET_1 ),
        summary : T_NET_EXTRA_DESCRIPTION( SendanorProductId.NET_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : NET_1_MONTHLY_PRICE,
                // sum: 1000*(1+DEFAULT_VAT_PERCENT),
                type : ProductPriceType.MONTHLY,
                discountPercent : NET_DISCOUNT_PERCENT_MONTHLY,
                discountFrom : NET_DISCOUNT_FROM,
                discountTo : NET_DISCOUNT_TO,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : NET_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 10000*(1+DEFAULT_VAT_PERCENT),
                type : ProductPriceType.YEARLY,
                discountPercent : NET_DISCOUNT_PERCENT_YEARLY,
                discountFrom : NET_DISCOUNT_FROM,
                discountTo : NET_DISCOUNT_TO,
            },
        ],
        features : [
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_10G,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TAMPERE_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_AVAILABLE,
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
            {
                id : ProductFeatureId.NETWORK_NET6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PUBLIC_INCLUDED,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : false,
        id : SendanorProductId.WEBRTC_1,
        type : ProductType.WEBRTC,
        title : T_NET_EXTRA_TITLE( SendanorProductId.WEBRTC_1 ),
        summary : T_NET_EXTRA_DESCRIPTION( SendanorProductId.WEBRTC_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEBRTC_1_MONTHLY_PRICE,
                // sum: 99.20, // 80 €/kk + ALV 24%
                type : ProductPriceType.MONTHLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEBRTC_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 992, // 66.67 €/kk + ALV 24%
                type : ProductPriceType.YEARLY,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEBRTC_1_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1999, // 44.78 €/kk + ALV
                type : ProductPriceType.YEARS_3,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEBRTC_1_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2604, // 35 €/kk + ALV
                type : ProductPriceType.YEARS_5,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : WEBRTC_1_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 3720, // 25 €/kk + ALV
                type : ProductPriceType.YEARS_10,
                discountPercent : VPS_DISCOUNT_PERCENT,
                discountFrom : VPS_DISCOUNT_FROM,
                discountTo : VPS_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_HOTEL_DISK_5,
        type : ProductType.WEB_HOTEL_EXTRA,
        title : T_WEB_EXTRA_TITLE( SendanorProductId.WEB_HOTEL_DISK_5 ),
        summary : T_WEB_EXTRA_SUMMARY( SendanorProductId.WEB_HOTEL_DISK_5 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_5_MONTHLY_PRICE,
                // sum: 25,
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_5_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 60,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_5_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_5_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 100,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_5_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 160,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 5,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.ANY,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_HOTEL_DISK_25,
        type : ProductType.WEB_HOTEL_EXTRA,
        title : T_WEB_EXTRA_TITLE( SendanorProductId.WEB_HOTEL_DISK_25 ),
        summary : T_WEB_EXTRA_SUMMARY( SendanorProductId.WEB_HOTEL_DISK_25 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_25_MONTHLY_PRICE,
                // sum: 25,
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_25_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 60,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_25_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_25_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 100,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_25_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 160,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 25,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.ANY,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_HOTEL_DISK_50,
        type : ProductType.WEB_HOTEL_EXTRA,
        title : T_WEB_EXTRA_TITLE( SendanorProductId.WEB_HOTEL_DISK_50 ),
        summary : T_WEB_EXTRA_SUMMARY( SendanorProductId.WEB_HOTEL_DISK_50 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_50_MONTHLY_PRICE,
                // sum: 25,
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_50_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 60,
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_50_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_50_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 100,
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_DISK_50_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 160,
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 50,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.ANY,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_SUPPORT_1,
        type : ProductType.WEB_HOTEL_EXTRA,
        title : T_WEB_EXTRA_TITLE( SendanorProductId.WEB_SUPPORT_1 ),
        summary : T_WEB_EXTRA_SUMMARY( SendanorProductId.WEB_SUPPORT_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_SUPPORT_1_MONTHLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 967.20, // 65 €/h
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1800*(1+DEFAULT_VAT_PERCENT), // 50 €/h
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2700*(1+DEFAULT_VAT_PERCENT), // 45 €/h
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 5160*(1+DEFAULT_VAT_PERCENT), // 43 €/h
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.SUPPORT_1H_M,
                category : ProductFeatureCategory.SUPPORT,
                value : 1,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_HOTEL_BACKUP_RESTORE_1,
        type : ProductType.WEB_HOTEL_EXTRA,
        title : T_WEB_EXTRA_TITLE( SendanorProductId.WEB_HOTEL_BACKUP_RESTORE_1 ),
        summary : T_WEB_EXTRA_SUMMARY( SendanorProductId.WEB_HOTEL_BACKUP_RESTORE_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_BACKUP_RESTORE_1_MONTHLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_BACKUP_RESTORE_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 520.80, // 1 x 6 x 70 €/h + ALV
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_BACKUP_RESTORE_1_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1450.80, // 3 x 6 x 65 €/h + ALV
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_BACKUP_RESTORE_1_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2232, // 5 x 6 x 60 €/h + ALV
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_BACKUP_RESTORE_1_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 4092, // 10 x 6 x 55 €/h + ALV
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.BACKUP_RESTORE_1H_M,
                category : ProductFeatureCategory.SUPPORT,
                value : true,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_USER_1,
        type : ProductType.WEB_HOTEL_EXTRA,
        title : T_WEB_EXTRA_TITLE( SendanorProductId.WEB_USER_1 ),
        summary : T_WEB_EXTRA_SUMMARY( SendanorProductId.WEB_USER_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_USER_1_MONTHLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_USER_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 520.80, // 1 x 6 x 70 €/h + ALV
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_USER_1_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1450.80, // 3 x 6 x 65 €/h + ALV
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_USER_1_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2232, // 5 x 6 x 60 €/h + ALV
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_USER_1_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 4092, // 10 x 6 x 55 €/h + ALV
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.USER_M,
                category : ProductFeatureCategory.SUPPORT,
                value : true,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_MYSQL_1,
        type : ProductType.WEB_HOTEL_EXTRA,
        title : T_WEB_EXTRA_TITLE( SendanorProductId.WEB_MYSQL_1 ),
        summary : T_WEB_EXTRA_SUMMARY( SendanorProductId.WEB_MYSQL_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_MYSQL_1_MONTHLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_MYSQL_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 520.80, // 1 x 6 x 70 €/h + ALV
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_MYSQL_1_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1450.80, // 3 x 6 x 65 €/h + ALV
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_MYSQL_1_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2232, // 5 x 6 x 60 €/h + ALV
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_MYSQL_1_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 4092, // 10 x 6 x 55 €/h + ALV
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DB_MYSQL_M,
                category : ProductFeatureCategory.SUPPORT,
                value : true,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.WEB_PSQL_1,
        type : ProductType.WEB_HOTEL_EXTRA,
        title : T_WEB_EXTRA_TITLE( SendanorProductId.WEB_PSQL_1 ),
        summary : T_WEB_EXTRA_SUMMARY( SendanorProductId.WEB_PSQL_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_PSQL_1_MONTHLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_PSQL_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 520.80, // 1 x 6 x 70 €/h + ALV
                type : ProductPriceType.YEARLY,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_PSQL_1_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1450.80, // 3 x 6 x 65 €/h + ALV
                type : ProductPriceType.YEARS_3,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_PSQL_1_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2232, // 5 x 6 x 60 €/h + ALV
                type : ProductPriceType.YEARS_5,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_WEB_HOTEL_PSQL_1_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 4092, // 10 x 6 x 55 €/h + ALV
                type : ProductPriceType.YEARS_10,
                discountPercent : WEB_DISCOUNT_PERCENT,
                discountFrom : WEB_DISCOUNT_FROM,
                discountTo : WEB_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DB_PSQL_M,
                category : ProductFeatureCategory.SUPPORT,
                value : true,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : false,
        id : SendanorProductId.ATLAS_18G,
        type : ProductType.ATLAS,
        title : T_ATLAS_PRODUCT_ATLAS_18G_TITLE,
        summary : T_ATLAS_PRODUCT_ATLAS_18G_DESCRIPTION,
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : ATLAS_18G_MONTHLY_PRICE,
                type : ProductPriceType.MONTHLY,
                discountPercent : ATLAS_DISCOUNT_PERCENT,
                discountFrom : ATLAS_DISCOUNT_FROM,
                discountTo : ATLAS_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : ATLAS_18G_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                type : ProductPriceType.YEARLY,
                discountPercent : ATLAS_DISCOUNT_PERCENT,
                discountFrom : ATLAS_DISCOUNT_FROM,
                discountTo : ATLAS_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : ATLAS_18G_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                type : ProductPriceType.YEARS_3,
                discountPercent : ATLAS_DISCOUNT_PERCENT,
                discountFrom : ATLAS_DISCOUNT_FROM,
                discountTo : ATLAS_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : ATLAS_18G_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                type : ProductPriceType.YEARS_5,
                discountPercent : ATLAS_DISCOUNT_PERCENT,
                discountFrom : ATLAS_DISCOUNT_FROM,
                discountTo : ATLAS_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : ATLAS_18G_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                type : ProductPriceType.YEARS_10,
                discountPercent : ATLAS_DISCOUNT_PERCENT,
                discountFrom : ATLAS_DISCOUNT_FROM,
                discountTo : ATLAS_DISCOUNT_TO,
                published: true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.VPS_TYPE,
                category : ProductFeatureCategory.VPS,
                value : VirtualizationType.LXC,
            },
            {
                id : ProductFeatureId.VPS_OS,
                category : ProductFeatureCategory.VPS,
                value : OperatingSystem.LINUX_UBUNTU_LTS_22_04,
            },
            {
                id : ProductFeatureId.DISK_SIZE,
                category : ProductFeatureCategory.DISK,
                value : 5,
            },
            {
                id : ProductFeatureId.DISK_TYPE,
                category : ProductFeatureCategory.DISK,
                value : DiskType.DISK_NVME_M2_SSD,
            },
            {
                id : ProductFeatureId.DISK_RAID,
                category : ProductFeatureCategory.DISK,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_BACKUP,
                category : ProductFeatureCategory.DISK,
                value : BackupType.INCLUDED_RESTORE_FREE,
            },
            {
                id : ProductFeatureId.DISK_USAGE,
                category : ProductFeatureCategory.DISK,
                value : DiskUsageType.DATA,
            },
            {
                id : ProductFeatureId.NETWORK_TYPE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkType.ETH_10G,
            },
            {
                id : ProductFeatureId.NETWORK_TRAFFIC,
                category : ProductFeatureCategory.NETWORK,
                value : 1024 * 10,
            },
            {
                id : ProductFeatureId.NETWORK_ZONE,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkZone.FI_TURKU_1,
            },
            {
                id : ProductFeatureId.NETWORK_IP4,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PRIVATE_NAT,
            },
            {
                id : ProductFeatureId.NETWORK_IP6,
                category : ProductFeatureCategory.NETWORK,
                value : NetworkIpType.PRIVATE_NAT,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : false,
        id : SendanorProductId.ATLAS_SUPPORT_1,
        type : ProductType.ATLAS_EXTRA,
        title : T_ATLAS_EXTRA_TITLE( SendanorProductId.ATLAS_SUPPORT_1 ),
        summary : T_ATLAS_EXTRA_SUMMARY( SendanorProductId.ATLAS_SUPPORT_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_ATLAS_SUPPORT_1_MONTHLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : ATLAS_EXTRA_DISCOUNT_PERCENT,
                discountFrom : ATLAS_EXTRA_DISCOUNT_FROM,
                discountTo : ATLAS_EXTRA_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_ATLAS_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 967.20, // 65 €/h
                type : ProductPriceType.YEARLY,
                discountPercent : ATLAS_EXTRA_DISCOUNT_PERCENT,
                discountFrom : ATLAS_EXTRA_DISCOUNT_FROM,
                discountTo : ATLAS_EXTRA_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_ATLAS_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1800*(1+DEFAULT_VAT_PERCENT), // 50 €/h
                type : ProductPriceType.YEARS_3,
                discountPercent : ATLAS_EXTRA_DISCOUNT_PERCENT,
                discountFrom : ATLAS_EXTRA_DISCOUNT_FROM,
                discountTo : ATLAS_EXTRA_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_ATLAS_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2700*(1+DEFAULT_VAT_PERCENT), // 45 €/h
                type : ProductPriceType.YEARS_5,
                discountPercent : ATLAS_EXTRA_DISCOUNT_PERCENT,
                discountFrom : ATLAS_EXTRA_DISCOUNT_FROM,
                discountTo : ATLAS_EXTRA_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_ATLAS_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 5160*(1+DEFAULT_VAT_PERCENT), // 43 €/h
                type : ProductPriceType.YEARS_10,
                discountPercent : ATLAS_EXTRA_DISCOUNT_PERCENT,
                discountFrom : ATLAS_EXTRA_DISCOUNT_FROM,
                discountTo : ATLAS_EXTRA_DISCOUNT_TO,
                published: true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.SUPPORT_1H_M,
                category : ProductFeatureCategory.SUPPORT,
                value : 1,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : false,
        id : SendanorProductId.ATLAS_USER_1,
        type : ProductType.ATLAS_EXTRA,
        title : T_ATLAS_EXTRA_TITLE( SendanorProductId.ATLAS_USER_1 ),
        summary : T_ATLAS_EXTRA_SUMMARY( SendanorProductId.ATLAS_USER_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_ATLAS_USER_1_MONTHLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : ATLAS_EXTRA_DISCOUNT_PERCENT,
                discountFrom : ATLAS_EXTRA_DISCOUNT_FROM,
                discountTo : ATLAS_EXTRA_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_ATLAS_USER_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 520.80, // 1 x 6 x 70 €/h + ALV
                type : ProductPriceType.YEARLY,
                discountPercent : ATLAS_EXTRA_DISCOUNT_PERCENT,
                discountFrom : ATLAS_EXTRA_DISCOUNT_FROM,
                discountTo : ATLAS_EXTRA_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_ATLAS_USER_1_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1450.80, // 3 x 6 x 65 €/h + ALV
                type : ProductPriceType.YEARS_3,
                discountPercent : ATLAS_EXTRA_DISCOUNT_PERCENT,
                discountFrom : ATLAS_EXTRA_DISCOUNT_FROM,
                discountTo : ATLAS_EXTRA_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_ATLAS_USER_1_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2232, // 5 x 6 x 60 €/h + ALV
                type : ProductPriceType.YEARS_5,
                discountPercent : ATLAS_EXTRA_DISCOUNT_PERCENT,
                discountFrom : ATLAS_EXTRA_DISCOUNT_FROM,
                discountTo : ATLAS_EXTRA_DISCOUNT_TO,
                published: true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_ATLAS_USER_1_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 4092, // 10 x 6 x 55 €/h + ALV
                type : ProductPriceType.YEARS_10,
                discountPercent : ATLAS_EXTRA_DISCOUNT_PERCENT,
                discountFrom : ATLAS_EXTRA_DISCOUNT_FROM,
                discountTo : ATLAS_EXTRA_DISCOUNT_TO,
                published: true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.USER_M,
                category : ProductFeatureCategory.SUPPORT,
                value : true,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.EMAIL_DISK_10,
        type : ProductType.EMAIL_EXTRA,
        title : T_EMAIL_EXTRA_TITLE( SendanorProductId.EMAIL_DISK_10 ),
        summary : T_EMAIL_EXTRA_SUMMARY( SendanorProductId.EMAIL_DISK_10 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_DISK_10_MONTHLY_PRICE,
                // sum: 15,
                type : ProductPriceType.MONTHLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_DISK_10_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 30,
                type : ProductPriceType.YEARLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_DISK_10_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 35,
                type : ProductPriceType.YEARS_3,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_DISK_10_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 50,
                type : ProductPriceType.YEARS_5,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_DISK_10_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 80,
                type : ProductPriceType.YEARS_10,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 10,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.ANY,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.EMAIL_DISK_50,
        type : ProductType.EMAIL_EXTRA,
        title : T_EMAIL_EXTRA_TITLE( SendanorProductId.EMAIL_DISK_50 ),
        summary : T_EMAIL_EXTRA_SUMMARY( SendanorProductId.EMAIL_DISK_50 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_DISK_50_MONTHLY_PRICE,
                // sum: 50,
                type : ProductPriceType.MONTHLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_DISK_50_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 80,
                type : ProductPriceType.YEARLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_DISK_50_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 100,
                type : ProductPriceType.YEARS_3,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_DISK_50_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 150,
                type : ProductPriceType.YEARS_5,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_DISK_50_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 200,
                type : ProductPriceType.YEARS_10,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.DISK_2_SIZE,
                category : ProductFeatureCategory.DISK_2,
                value : 50,
            },
            {
                id : ProductFeatureId.DISK_2_RAID,
                category : ProductFeatureCategory.DISK_2,
                value : RaidType.RAID_MIRROR,
            },
            {
                id : ProductFeatureId.DISK_2_BACKUP,
                category : ProductFeatureCategory.DISK_2,
                value : BackupType.INCLUDED_WITH_RESTORE_FEE,
            },
            {
                id : ProductFeatureId.DISK_2_USAGE,
                category : ProductFeatureCategory.DISK_2,
                value : DiskUsageType.ANY,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.EMAIL_SUPPORT_1,
        type : ProductType.EMAIL_EXTRA,
        title : T_EMAIL_EXTRA_TITLE( SendanorProductId.EMAIL_SUPPORT_1 ),
        summary : T_EMAIL_EXTRA_SUMMARY( SendanorProductId.EMAIL_SUPPORT_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_SUPPORT_1_MONTHLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 967.20, // 65 €/h
                type : ProductPriceType.YEARLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1800*(1+DEFAULT_VAT_PERCENT), // 50 €/h
                type : ProductPriceType.YEARS_3,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2700*(1+DEFAULT_VAT_PERCENT), // 45 €/h
                type : ProductPriceType.YEARS_5,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_SUPPORT_1_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 5160*(1+DEFAULT_VAT_PERCENT), // 43 €/h
                type : ProductPriceType.YEARS_10,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.SUPPORT_1H_M,
                category : ProductFeatureCategory.SUPPORT,
                value : 1,
            },
        ],
    },
    {
        stockAmount : 999, stockEnabled : false, onHold : false, published : true,
        id : SendanorProductId.EMAIL_BACKUP_RESTORE_1,
        type : ProductType.EMAIL_EXTRA,
        title : T_EMAIL_EXTRA_TITLE( SendanorProductId.EMAIL_BACKUP_RESTORE_1 ),
        summary : T_EMAIL_EXTRA_SUMMARY( SendanorProductId.EMAIL_BACKUP_RESTORE_1 ),
        prices : [
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_BACKUP_RESTORE_1_MONTHLY_PRICE,
                // sum: 86.80, // 70 €/h
                type : ProductPriceType.MONTHLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_BACKUP_RESTORE_1_MONTHLY_PRICE * FROM_MONTH_TO_YEAR_FACTOR,
                // sum: 520.80, // 1 x 6 x 70 €/h + ALV
                type : ProductPriceType.YEARLY,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_BACKUP_RESTORE_1_MONTHLY_PRICE * FROM_MONTH_TO_3_YEAR_FACTOR,
                // sum: 1450.80, // 3 x 6 x 65 €/h + ALV
                type : ProductPriceType.YEARS_3,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_BACKUP_RESTORE_1_MONTHLY_PRICE * FROM_MONTH_TO_5_YEAR_FACTOR,
                // sum: 2232, // 5 x 6 x 60 €/h + ALV
                type : ProductPriceType.YEARS_5,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
            {
                vatPercent : DEFAULT_VAT_PERCENT,
                sum : EXTRA_EMAIL_BACKUP_RESTORE_1_MONTHLY_PRICE * FROM_MONTH_TO_10_YEAR_FACTOR,
                // sum: 4092, // 10 x 6 x 55 €/h + ALV
                type : ProductPriceType.YEARS_10,
                discountPercent : EMAIL_DISCOUNT_PERCENT,
                discountFrom : EMAIL_DISCOUNT_FROM,
                discountTo : EMAIL_DISCOUNT_TO,
                published : true,
            },
        ],
        features : [
            {
                id : ProductFeatureId.BACKUP_RESTORE_1H_M,
                category : ProductFeatureCategory.SUPPORT,
                value : true,
            },
        ],
    },

    ...map(
            SHORT_DOMAINS_ON_SALE,
            (domain: string) : Product =>
                SendanorProductUtils.createDomainProduct(
                    domain,
                    ProductType.DOMAIN,
                    [
                        {
                            eurPriceIncludingVat: SHORT_DOMAIN_PRICE_INCLUDING_VAT_1_YEAR,
                            vatPercent: SHORT_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARLY,
                        },
                        {
                            eurPriceIncludingVat: SHORT_DOMAIN_PRICE_INCLUDING_VAT_3_YEARS,
                            vatPercent: SHORT_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARS_3,
                        },
                        {
                            eurPriceIncludingVat: SHORT_DOMAIN_PRICE_INCLUDING_VAT_5_YEARS,
                            vatPercent: SHORT_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARS_5,
                        },
                        {
                            eurPriceIncludingVat: SHORT_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS,
                            vatPercent: SHORT_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARS_10,
                        }
                    ],
                    true,
                    0,
                )
    ),

    ...map(
            PREMIUM_DOMAINS_ON_SALE,
            (domain: string) : Product =>
                SendanorProductUtils.createDomainProduct(
                    domain,
                    ProductType.DOMAIN,
                    [
                        {
                            eurPriceIncludingVat: PREMIUM_DOMAIN_PRICE_INCLUDING_VAT_1_YEAR,
                            vatPercent: PREMIUM_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARLY,
                        },
                        {
                            eurPriceIncludingVat: PREMIUM_DOMAIN_PRICE_INCLUDING_VAT_3_YEARS,
                            vatPercent: PREMIUM_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARS_3,
                        },
                        {
                            eurPriceIncludingVat: PREMIUM_DOMAIN_PRICE_INCLUDING_VAT_5_YEARS,
                            vatPercent: PREMIUM_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARS_5,
                        },
                        {
                            eurPriceIncludingVat: PREMIUM_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS,
                            vatPercent: PREMIUM_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARS_10,
                        }
                    ],
                    true,
                    0,
                )
    ),

    ...map(
            STANDARD_DOMAINS_ON_SALE,
            (domain: string) : Product =>
                SendanorProductUtils.createDomainProduct(
                    domain,
                    ProductType.DOMAIN,
                    [
                        {
                            eurPriceIncludingVat: STANDARD_DOMAIN_PRICE_INCLUDING_VAT_1_YEAR,
                            vatPercent: STANDARD_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARLY,
                        },
                        {
                            eurPriceIncludingVat: STANDARD_DOMAIN_PRICE_INCLUDING_VAT_3_YEARS,
                            vatPercent: STANDARD_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARS_3,
                        },
                        {
                            eurPriceIncludingVat: STANDARD_DOMAIN_PRICE_INCLUDING_VAT_5_YEARS,
                            vatPercent: STANDARD_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARS_5,
                        },
                        {
                            eurPriceIncludingVat: STANDARD_DOMAIN_PRICE_INCLUDING_VAT_10_YEARS,
                            vatPercent: STANDARD_DOMAIN_VAT_PERCENT,
                            priceType: ProductPriceType.YEARS_10,
                        }
                    ],
                    true,
                    0,
                )
    ),

    // Sendanor: Lenovo laptop: Lenovo ThinkPad X13 Gen 2 - 13,3", Win 10 Pro (20XJS14W00)
    SendanorProductUtils.createOutletProduct(
        'lenovo-20XJS14W00-1',
        480,
        DEFAULT_VAT_PERCENT,
        true,
        1,
        1,
    ),

    // HG: Apple laptop: MacBook Pro 13 (2020) M1, 8-coreCPU/8-coreGPU/16GB/1TB, tähtiharmaa (FVFGD09DQ05P)
    SendanorProductUtils.createOutletProduct(
        'AppleMacBookPro2020-1',
        760,
        DEFAULT_VAT_PERCENT,
        true,
        1,
        0,
    ),

    // HG: Apple laptop: Apple MacBook Pro 14 (2023) M2 Pro 10-coreCPU/16-coreGPU/16GB/512GB/tähtiharmaa
    SendanorProductUtils.createOutletProduct(
        'AppleMacBookPro14m2pro2023-1',
        1810,
        DEFAULT_VAT_PERCENT,
        true,
        1,
        0,
    ),

    // HG: Apple laptop: MacBook Air (2020), "Apple MacBook Air (2020) M1 8-coreCPU/8-coreGPU/16GB/1TB/tähtiharmaa
    SendanorProductUtils.createOutletProduct(
        'AppleMacBookAir2020-1',
        810,
        DEFAULT_VAT_PERCENT,
        true,
        2,
        0,
    ),

    // HG: Lenovo laptop: Thinkpad P15v Gen 2 (HG)
    SendanorProductUtils.createOutletProduct(
        'LenovoThinkpadP15vGen2-1',
        1305,
        DEFAULT_VAT_PERCENT,
        true,
        2,
        0,
    ),

    // Sendanor & HG: Lenovo monitor: LenovoThinkVisionP32
    SendanorProductUtils.createOutletProduct(
        'LenovoThinkVisionP32p20-1',
        418,
        DEFAULT_VAT_PERCENT,
        true,
        0,
        0,
    ),

    // HG: Samsung Q70BA 55" QLED -televisio
    SendanorProductUtils.createOutletProduct(
        'SamsungQ70BA55-1',
        435,
        DEFAULT_VAT_PERCENT,
        true,
        1,
        0,
    ),

    // HG: Jabra Speak 710 MS -konferenssikaiutin
    SendanorProductUtils.createOutletProduct(
        'JabraSpeak710MS-1',
        149,
        DEFAULT_VAT_PERCENT,
        true,
        2,
        0,
    ),

    // HG: JBL Tune 760NC -vastamelukuulokkeet, musta
    SendanorProductUtils.createOutletProduct(
        'JBLTune760NC-1',
        30,
        DEFAULT_VAT_PERCENT,
        true,
        2,
        0,
    ),

    // HG: Näyttö ThinkVision P27h-10
    SendanorProductUtils.createOutletProduct(
        'LenovoThinkVisionP27h10-1',
        150,
        DEFAULT_VAT_PERCENT,
        true,
        2,
        0,
    ),

    // HG: Lenovo Q24h-10 24" -näyttö
    SendanorProductUtils.createOutletProduct(
        'LenovoQ24h10-1',
        150,
        DEFAULT_VAT_PERCENT,
        true,
        4,
        0,
    ),

];

export const PRODUCTS : readonly Product[] = filter(
    ALL_PRODUCTS,
    ( item : Product ) : boolean => !!item.published && !item.onHold,
);

function margeFeatures (
    id: string,
    ...lists : readonly (readonly ProductFeature[])[]
): readonly ProductFeature[] {
    return reduce(
        lists,
        (list: readonly ProductFeature[], b: readonly ProductFeature[]) : readonly ProductFeature[] => {
            return [
                ...list,
                ...map(
                    b,
                    (item: ProductFeature): ProductFeature => {
                        const j = find(list, i => isEqual(i, item))
                        if (j) {
                            LOG.warn(`Warning! Duplicate value for ${id}: `, j, item);
                        }
                        return j ? j : item;
                    }
                ),
            ];
        },
        []
    );
}